<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :actions="actions"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <!-- Add New Bus Change Dialog -->
    <v-dialog persistent v-model="addDialog" max-width="1200">
      <v-card class="bus-change-dialog">
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-bus-clock</v-icon>
          <span class="dialog-title">{{
            isEditing ? "Edit Bus Change" : "New Bus Change"
          }}</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 dialog-body">
          <!-- Student Source Toggle (school vs day camp enrollment) -->
          <v-layout
            v-if="!isEditing && !selectedStudent"
            row
            wrap
            align-center
            class="mb-3"
          >
            <v-flex xs12>
              <div class="caption grey--text mb-1">Student Source</div>
              <v-btn-toggle
                v-model="studentSource"
                mandatory
                class="search-toggle"
                @change="onStudentSourceChange"
              >
                <v-btn flat small value="school">
                  <v-icon small class="mr-1">mdi-school</v-icon>School
                </v-btn>
                <v-btn flat small value="daycamp">
                  <v-icon small class="mr-1">mdi-tent</v-icon>Day Camp
                </v-btn>
              </v-btn-toggle>
              <span v-if="isDayCamp" class="caption grey--text ml-3">
                Only students enrolled in the current day camp season are shown.
              </span>
            </v-flex>
          </v-layout>

          <!-- Account Search Section (only for new and when no account selected) -->
          <v-card
            v-if="!isEditing && !selectedAccount"
            outlined
            class="pa-3 mb-3 form-section-card"
          >
            <div class="section-header mb-2">
              <v-icon small class="mr-1">mdi-account-search</v-icon>
              Search Account
            </div>

            <!-- Phone Number Search -->
            <v-layout row wrap class="mb-1">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="phoneSearch"
                  label="Search by Phone Number"
                  prepend-inner-icon="mdi-phone"
                  outlined
                  dense
                  clearable
                  mask="###-###-####"
                  @input="searchByPhone"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- Search Name Mode Toggle -->
            <v-layout row wrap align-center class="mb-2">
              <v-flex xs12 sm6 class="pr-2">
                <div class="caption grey--text mb-1">Or Search By Name</div>
                <v-btn-toggle
                  v-model="searchNameMode"
                  mandatory
                  class="search-toggle"
                >
                  <v-btn flat small value="yiddish">אידיש</v-btn>
                  <v-btn flat small value="english">English</v-btn>
                </v-btn-toggle>
              </v-flex>
            </v-layout>

            <!-- Account Search Fields -->
            <v-layout row wrap class="mb-0">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="lastNameSearch"
                  :label="
                    searchNameMode === 'yiddish' ? 'לעצטע נאמען' : 'Last Name'
                  "
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  clearable
                  @input="searchByName"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="firstNameSearch"
                  :label="
                    searchNameMode === 'yiddish' ? 'טאטעס נאמען' : 'Father Name'
                  "
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  clearable
                  @input="searchByName"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- Address Search -->
            <v-layout row wrap class="mb-1 mt-2">
              <v-flex xs12>
                <div class="caption grey--text mb-1">Or Search By Address</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap class="mb-0">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="houseNumberSearch"
                  label="House Number"
                  prepend-inner-icon="mdi-home"
                  outlined
                  dense
                  clearable
                  @input="searchByAddress"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="streetSearch"
                  label="Street Name"
                  prepend-inner-icon="mdi-road"
                  outlined
                  dense
                  clearable
                  @input="searchByAddress"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- Account List -->
            <div class="account-list-container mb-3" v-if="accounts.length > 0">
              <div class="caption grey--text my-2 px-2">
                Select an account ({{ accounts.length }} found)
              </div>
              <v-list dense class="account-list">
                <v-list-tile
                  v-for="account in accounts"
                  :key="account.accountID"
                  @click="selectAccount(account)"
                  class="account-item"
                >
                  <v-list-tile-avatar>
                    <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <strong>{{ account.name }}</strong>
                      <span class="grey--text ml-2">({{ account.hname }})</span>
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      Account ID: {{ account.accountID }}
                      <span v-if="account.homePhone" class="ml-1">
                        | {{ account.homePhone }}
                      </span>
                      <span v-if="account.cellPhone" class="ml-1">
                        | {{ account.cellPhone }}
                      </span>
                      <span v-if="account.address1" class="ml-1">
                        | {{ account.address1 }}
                      </span>
                      <span v-if="account.busAddress1" class="ml-1">
                        | Bus: {{ account.busAddress1 }}
                      </span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </div>

            <!-- No Accounts Message -->
            <v-alert
              v-if="showNoAccountsMessage && accounts.length === 0"
              type="warning"
              outline
              dense
              value="true"
              class="mb-0"
            >
              No accounts found. Please try a different search.
            </v-alert>
          </v-card>

          <!-- Selected Account and Student Selection (only for new) -->
          <v-card
            v-if="!isEditing && selectedAccount && !selectedStudent"
            outlined
            class="pa-3 mb-3 form-section-card"
          >
            <!-- Selected Account Card -->
            <v-card class="selected-account-card mb-4" outlined>
              <v-card-text class="pa-0">
                <v-layout align-center>
                  <v-avatar color="rgb(92, 184, 96)" size="40" class="mr-2">
                    <v-icon dark size="24">mdi-account-box</v-icon>
                  </v-avatar>
                  <v-flex>
                    <div class="body-2 font-weight-medium">
                      {{ selectedAccount.name }}
                      <span class="caption grey--text ml-1"
                        >({{ selectedAccount.hname }})</span
                      >
                    </div>
                    <div class="caption grey--text">
                      <strong>Account ID:</strong>
                      {{ selectedAccount.accountID }}
                      <span v-if="selectedAccount.address1" class="ml-2">
                        | {{ selectedAccount.address1 }}
                      </span>
                    </div>
                  </v-flex>
                  <v-btn icon small @click="clearAccount">
                    <v-icon color="grey">mdi-close-circle</v-icon>
                  </v-btn>
                </v-layout>
              </v-card-text>
            </v-card>

            <!-- Student List from Selected Account -->
            <div class="section-header mb-3">
              <v-icon small class="mr-1">mdi-account-multiple</v-icon>
              Select Student
            </div>

            <div v-if="loadingStudents" class="text-center pa-4">
              <v-progress-circular
                indeterminate
                color="rgb(92, 184, 96)"
              ></v-progress-circular>
              <div class="mt-2 grey--text">Loading students...</div>
            </div>

            <v-alert
              v-else-if="students.length === 0"
              type="info"
              outline
              dense
              value="true"
              class="mb-3"
            >
              <span v-if="isDayCamp">
                No day camp enrolled students found for this account.
              </span>
              <span v-else-if="userBranch">
                No students found for this account in branch{{
                  userBranch.includes(",") ? "es" : ""
                }}
                {{ userBranch }}.
              </span>
              <span v-else>
                No students found for this account.
              </span>
            </v-alert>

            <div v-else class="student-list-container">
              <v-list
                dense
                :two-line="!isDayCamp"
                :three-line="isDayCamp"
                class="student-list"
              >
                <v-list-tile
                  v-for="student in students"
                  :key="student.studentID"
                  @click="selectStudent(student)"
                  class="student-item"
                >
                  <v-list-tile-avatar>
                    <v-icon color="rgb(92, 184, 96)">mdi-account-circle</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <strong>{{ getStudentHebName(student) }}</strong>
                      <span class="grey--text ml-2"
                        >({{ student.engName }})</span
                      >
                    </v-list-tile-title>
                    <template v-if="isDayCamp">
                      <v-list-tile-sub-title>
                        <span v-if="student.class">
                          Grade: {{ student.class }}
                        </span>
                        <span v-if="student.building" class="ml-2">
                          | Building: {{ student.building }}
                        </span>
                        <span v-if="getStudentClassExtra(student)" class="ml-2">
                          | {{ getStudentClassExtra(student) }}
                        </span>
                      </v-list-tile-sub-title>
                      <v-list-tile-sub-title>
                        <v-icon x-small class="mr-1">mdi-tent</v-icon>
                        {{ getEnrollmentDisplay(student) }}
                      </v-list-tile-sub-title>
                    </template>
                    <v-list-tile-sub-title v-else>
                      Grade: {{ student.class }} | Building:
                      {{ student.building }}
                      <span v-if="student.branch" class="ml-2">
                        | Branch: {{ student.branch }}
                      </span>
                      <span
                        v-if="student.teacherH || student.teacherH2"
                        class="ml-2"
                      >
                        | Teacher:
                        {{
                          [student.teacherH, student.teacherH2]
                            .filter((t) => t)
                            .join(" / ")
                        }}
                      </span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </div>
          </v-card>

          <!-- Selected Student Card (shows when student is selected) -->
          <div v-if="selectedStudent">
            <v-card class="selected-student-card mb-4" outlined>
              <v-card-text class="pa-0">
                <v-layout align-center>
                  <v-avatar color="rgb(92, 184, 96)" size="40" class="mr-2">
                    <v-icon dark size="24">mdi-account</v-icon>
                  </v-avatar>
                  <v-flex>
                    <v-layout row wrap>
                      <v-flex xs12 sm6 class="pr-2">
                        <div class="body-2 font-weight-medium pb-0">
                          {{ selectedStudent.hebName || selectedStudent.studentName }}
                          <span
                            v-if="
                              selectedStudent.hebName &&
                                selectedStudent.studentName
                            "
                            class="caption grey--text ml-1"
                            >({{ selectedStudent.studentName }})</span
                          >
                        </div>
                        <div class="caption grey--text">
                          <strong>Student ID:</strong>
                          {{ selectedStudent.studentID }} |
                          <strong>Building:</strong>
                          {{ selectedStudent.building }} |
                          <strong>Class:</strong> {{ selectedStudent.class }}-{{
                            selectedStudent.section
                          }}
                        </div>
                        <div
                          v-if="isDayCamp && selectedStudent.regType"
                          class="caption daycamp-enrollment-line"
                        >
                          <v-icon x-small color="rgb(92, 184, 96)" class="mr-1"
                            >mdi-tent</v-icon
                          >
                          {{ getEnrollmentDisplay(selectedStudent) }}
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <div
                          class="caption grey--text"
                          v-if="
                            selectedStudent.teacherH ||
                              selectedStudent.teacherH2
                          "
                        >
                          <v-icon x-small class="mr-1">mdi-account-tie</v-icon>
                          {{ getTeacherDisplay(selectedStudent) }}
                        </div>
                        <div
                          class="caption grey--text"
                          v-else-if="selectedStudent.room"
                        >
                          <v-icon x-small class="mr-1">mdi-door</v-icon>
                          Room: {{ selectedStudent.room }}
                        </div>
                        <div
                          class="caption grey--text"
                          v-if="
                            selectedStudent.address1 ||
                              (selectedAccount && selectedAccount.address1)
                          "
                        >
                          <v-icon x-small class="mr-1">mdi-home</v-icon>
                          {{
                            selectedStudent.address1 ||
                              (selectedAccount && selectedAccount.address1)
                          }}
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-btn v-if="!isEditing" icon small @click="clearStudent">
                    <v-icon color="grey">mdi-close-circle</v-icon>
                  </v-btn>
                </v-layout>
              </v-card-text>
            </v-card>
          </div>

          <v-layout v-if="selectedStudent" row wrap>
            <v-flex xs12 md3 class="pr-2">
              <v-card
                v-if="
                  isEditing && editingRecordInfo && editingRecordInfo.recordingURL
                "
                outlined
                class="pa-2 original-request-card mb-2"
              >
                <div class="recording-header">
                  <v-icon small color="#ff6b6b" class="recording-icon"
                    >mdi-microphone</v-icon
                  >
                  <span class="recording-label">Original Request</span>
                </div>
                <audio
                  ref="audioPlayer"
                  controls
                  controlsList="nodownload"
                  :src="getPlayableRecordingUrl(editingRecordInfo.recordingURL)"
                  class="inline-audio-player mt-1"
                ></audio>
              </v-card>
              <v-card outlined class="pa-2 compact-info">
                <div class="section-header mb-2">
                  <v-icon x-small class="mr-1">mdi-account</v-icon>
                  Info
                </div>
                <div class="info-row">
                  <span class="info-label">Student:</span>
                  <span class="info-value">{{
                    selectedStudent.studentName || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ID:</span>
                  <span class="info-value">{{
                    selectedStudent.studentID || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Source:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.source
                      ? editingRecordInfo.source
                      : "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Entered:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.enteredOn
                      ? formatDateTime(editingRecordInfo.enteredOn)
                      : "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Entered By:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.enteredBy
                      ? editingRecordInfo.enteredBy
                      : "-"
                  }}</span>
                </div>
                <div
                  class="info-row"
                  v-if="editingRecordInfo && editingRecordInfo.callerID"
                >
                  <span class="info-label">Caller ID:</span>
                  <span class="info-value">{{
                    formatPhoneNumber(editingRecordInfo.callerID)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Home:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.homePhone
                      ? editingRecordInfo.homePhone
                      : "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Cell:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.cellPhone
                      ? editingRecordInfo.cellPhone
                      : "-"
                  }}</span>
                </div>
              </v-card>
              <v-card outlined class="pa-2 mt-2 form-section-card">
                <div class="d-flex align-center mb-2">
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="editingExtraInfo"
                    small
                    outline
                    color="grey darken-1"
                    class="my-0 mr-1"
                    style="min-width: 0; padding: 0 8px;"
                    @click="cancelExtraInfoEdit"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                  <v-btn
                    small
                    outline
                    :color="editingExtraInfo ? 'success' : 'grey darken-1'"
                    class="my-0 ml-0"
                    style="min-width: 0; padding: 0 8px;"
                    :loading="savingExtraInfo"
                    @click="toggleExtraInfoEdit"
                  >
                    <v-icon left small>{{
                      editingExtraInfo ? "mdi-check" : "mdi-pencil"
                    }}</v-icon>
                    {{ editingExtraInfo ? "Done" : "Edit" }}
                  </v-btn>
                </div>
                <div v-if="!editingExtraInfo">
                  <div class="info-row">
                    <span class="info-label">Geo Phone:</span>
                    <span class="info-value">{{
                      newBusChange.geoPhoneNumber || "-"
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Note:</span>
                    <span class="info-value info-value-full">{{
                      newBusChange.note || "-"
                    }}</span>
                  </div>
                </div>
                <div v-else>
                  <v-text-field
                    v-model="newBusChange.geoPhoneNumber"
                    label="Geo Tracking Phone"
                    prepend-inner-icon="mdi-phone-in-talk"
                    outlined
                    dense
                    hide-details
                    class="mb-2"
                    :rules="[rules.phoneNumber]"
                    mask="###-###-####"
                    autocomplete="no-password"
                  ></v-text-field>
                  <v-textarea
                    v-model="newBusChange.note"
                    label="Note"
                    prepend-inner-icon="mdi-note-text"
                    outlined
                    dense
                    hide-details
                    rows="2"
                    autocomplete="no-password"
                  ></v-textarea>
                </div>
              </v-card>
            </v-flex>

            <!-- Bus Change Form Sections -->
            <v-flex xs12 md6 class="px-2">
              <!-- Dates -->
              <v-card outlined class="pa-3 mb-3 form-section-card">
                <div class="section-header mb-2">
                  <v-icon small class="mr-1">mdi-calendar-range</v-icon>
                  Dates
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    color="success"
                    outline
                    class="my-0"
                    @click="setTodayOnlyAfternoon"
                  >
                    <v-icon left small>mdi-weather-sunset</v-icon>
                    Today Only Afternoon
                  </v-btn>
                </div>
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="pr-2">
                    <div class="caption period-title mb-1">Starting Run</div>
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="newBusChange.startDate"
                          label="Start Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                          :rules="[rules.required]"
                          ref="startDate"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="newBusChange.startDate"
                        color="rgb(92, 184, 96)"
                        @input="onStartDateChange"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="newBusChange.startPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-up"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <div class="caption period-title mb-1">Ending After</div>
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="newBusChange.endDate"
                          label="End Date"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          class="mb-2"
                          v-on="on"
                          :rules="[rules.required]"
                          ref="endDate"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="newBusChange.endDate"
                        color="rgb(92, 184, 96)"
                        :min="newBusChange.startDate"
                        @input="endDateMenu = false"
                      ></v-date-picker>
                    </v-menu>
                    <v-select
                      v-model="newBusChange.endPeriod"
                      :items="periodOptions"
                      item-text="label"
                      item-value="value"
                      label="Run *"
                      prepend-inner-icon="mdi-weather-sunset-down"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.required]"
                    ></v-select>
                  </v-flex>
                </v-layout>
                <div
                  class="caption grey--text text--darken-1 mt-3 mb-2 period-helper-text"
                >
                  Morning Pickup: Includes the trip TO school. Afternoon
                  Dropoff: Includes the trip FROM school.
                </div>
                <v-alert
                  v-if="deadlineWarningMessage"
                  type="warning"
                  outline
                  dense
                  value="true"
                  class="mb-2 pa-2"
                >
                  {{ deadlineWarningMessage }}
                </v-alert>
                <v-alert
                  :value="!!enrollmentDateWarning"
                  type="warning"
                  outline
                  dense
                  class="mb-0 pa-2"
                >
                  {{ enrollmentDateWarning }}
                </v-alert>
              </v-card>

              <!-- Destination Info -->
              <v-card outlined class="pa-3 mb-3 form-section-card">
                <div class="section-header mb-3">
                  <v-icon small class="mr-1">mdi-map-marker</v-icon>
                  Destination Info
                </div>
                <v-text-field
                  v-model="newBusChange.busChangeAddress"
                  label="Destination Address *"
                  prepend-inner-icon="mdi-map-marker"
                  outlined
                  dense
                  hide-details
                  :rules="[rules.required]"
                  ref="busChangeAddress"
                  autocomplete="no-password"
                  class="mb-2"
                ></v-text-field>
                <v-layout row wrap class="mb-2">
                  <v-flex xs12 sm8 class="pr-1">
                    <v-text-field
                      v-model="newBusChange.busChangePhoneNumber"
                      label="Destination Phone"
                      prepend-inner-icon="mdi-phone"
                      outlined
                      dense
                      hide-details
                      :rules="[rules.phoneNumber]"
                      ref="busChangePhoneNumber"
                      mask="###-###-####"
                      autocomplete="no-password"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm4 class="pl-1">
                    <v-btn
                      color="success"
                      outline
                      block
                      class="mt-0"
                      :disabled="!newBusChange.busChangePhoneNumber"
                      @click="searchAccountByPhone"
                    >
                      <v-icon left small>mdi-magnify</v-icon>
                      Search
                    </v-btn>
                  </v-flex>
                </v-layout>
                <v-text-field
                  v-model="newBusChange.busChangeName"
                  label="Family Name"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  hide-details
                  autocomplete="no-password"
                ></v-text-field>
                <div
                  v-if="newBusChange.busChangeAccountID"
                  class="caption grey--text mt-1 mb-2"
                >
                  Account ID: {{ newBusChange.busChangeAccountID }}
                </div>
                <v-layout row wrap class="mt-2">
                  <v-flex xs12 sm6 class="pr-2">
                    <v-text-field
                      v-model="newBusChange.notificationPhoneNumber"
                      label="Notification Phone"
                      prepend-inner-icon="mdi-bell"
                      outlined
                      dense
                      :rules="[rules.phoneNumber]"
                      mask="###-###-####"
                      autocomplete="no-password"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <v-select
                      v-model="newBusChange.notificationType"
                      :items="['MESSAGE', 'CALL']"
                      label="Notification Type"
                      prepend-inner-icon="mdi-message-alert"
                      outlined
                      dense
                      clearable
                    ></v-select>
                  </v-flex>
                </v-layout>
              </v-card>

              <!-- Bus -->
              <v-card outlined class="pa-3 mb-1 form-section-card">
                <div class="section-header mb-3">
                  <v-icon small class="mr-1">mdi-bus</v-icon>
                  Bus
                </div>
                <v-layout row wrap>
                  <v-flex xs12 sm6 class="pr-2">
                    <v-autocomplete
                      v-model="selectedPickupRoute"
                      :items="filteredPickupRoutes"
                      :filter="customRouteFilter"
                      item-text="description"
                      item-value="id"
                      label="Pickup Route"
                      prepend-inner-icon="mdi-bus-clock"
                      outlined
                      dense
                      clearable
                      :loading="loadingBusRoutes"
                      placeholder="Start typing to search..."
                      return-object
                      @change="onPickupRouteChange"
                      class="route-autocomplete"
                      :menu-props="{ bottom: true, offsetY: true }"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="route-selection-wrapper">
                          <div class="route-selection-main">
                            {{ item.description }}
                            <span
                              v-if="getRouteBusNumber(item)"
                              class="route-detail-chip"
                              :style="getRouteChipStyle(item)"
                            >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                          </div>
                          <div v-if="item.driver" class="route-selection-driver">
                            <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                          </div>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content
                          class="route-list-item"
                          :style="
                            getRouteColor(item.color)
                              ? `border-left: 4px solid ${getRouteColor(
                                  item.color
                                )}`
                              : ''
                          "
                        >
                          <v-list-tile-title>
                            <strong>{{ item.description }}</strong>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span v-if="getRouteBusNumber(item)" class="mr-2">
                              <v-icon x-small color="grey darken-1">mdi-bus</v-icon>
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                            <span v-if="item.driver" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-account</v-icon
                              >
                              {{ item.driver }}
                            </span>
                            <span v-if="item.color" class="mr-2">
                              <v-icon
                                x-small
                                :style="
                                  getRouteColor(item.color)
                                    ? `color: ${getRouteColor(item.color)}`
                                    : ''
                                "
                                >mdi-circle</v-icon
                              >
                              {{ item.color }}
                            </span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                    <div v-if="selectedPickupRoute" class="geo-stop-picker mt-2">
                      <v-autocomplete
                        v-model="selectedPickupStopNumber"
                        :items="pickupRouteStops"
                        item-text="label"
                        item-value="stopNumber"
                        label="Pickup Geo Stop"
                        prepend-inner-icon="mdi-map-marker-radius"
                        outlined
                        dense
                        clearable
                        :loading="loadingPickupStops"
                        :disabled="loadingPickupStops"
                        placeholder="Select stop on this bus..."
                        :menu-props="{ bottom: true, offsetY: true, maxHeight: 320 }"
                        :error="!!selectedPickupRoute && !selectedPickupStopNumber"
                        :hint="
                          selectedPickupStopNumber
                            ? 'Used for GeoTrack'
                            : 'Required for GeoTrack sync'
                        "
                        persistent-hint
                        @change="onPickupStopNumberChange"
                      >
                        <template v-slot:selection="{ item }">
                          <span class="geo-stop-selection">
                            <span class="geo-stop-badge">{{ item.stopNumber }}</span>
                            <span class="geo-stop-address">{{
                              item.stopAddress || item.label
                            }}</span>
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-tile-content>
                            <v-list-tile-title class="geo-stop-item-title">
                              <span class="geo-stop-badge">{{
                                item.stopNumber
                              }}</span>
                              {{ item.stopAddress }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-flex>
                  <v-flex xs12 sm6 class="pl-2">
                    <v-autocomplete
                      v-model="selectedDropoffRoute"
                      :items="filteredDropoffRoutes"
                      :filter="customRouteFilter"
                      item-text="description"
                      item-value="id"
                      label="Dropoff Route"
                      prepend-inner-icon="mdi-bus-clock"
                      outlined
                      dense
                      clearable
                      :loading="loadingBusRoutes"
                      placeholder="Start typing to search..."
                      return-object
                      @change="onDropoffRouteChange"
                      class="route-autocomplete"
                      :menu-props="{ bottom: true, offsetY: true }"
                    >
                      <template v-slot:selection="{ item }">
                        <div class="route-selection-wrapper">
                          <div class="route-selection-main">
                            {{ item.description }}
                            <span
                              v-if="getRouteBusNumber(item)"
                              class="route-detail-chip"
                              :style="getRouteChipStyle(item)"
                            >
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                          </div>
                          <div v-if="item.driver" class="route-selection-driver">
                            <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                          </div>
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-tile-content
                          class="route-list-item"
                          :style="
                            getRouteColor(item.color)
                              ? `border-left: 4px solid ${getRouteColor(
                                  item.color
                                )}`
                              : ''
                          "
                        >
                          <v-list-tile-title>
                            <strong>{{ item.description }}</strong>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>
                            <span v-if="getRouteBusNumber(item)" class="mr-2">
                              <v-icon x-small color="grey darken-1">mdi-bus</v-icon>
                              Bus #{{ getRouteBusNumber(item) }}
                            </span>
                            <span v-if="item.driver" class="mr-2">
                              <v-icon x-small color="grey darken-1"
                                >mdi-account</v-icon
                              >
                              {{ item.driver }}
                            </span>
                            <span v-if="item.color" class="mr-2">
                              <v-icon
                                x-small
                                :style="
                                  getRouteColor(item.color)
                                    ? `color: ${getRouteColor(item.color)}`
                                    : ''
                                "
                                >mdi-circle</v-icon
                              >
                              {{ item.color }}
                            </span>
                          </v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                    <div v-if="selectedDropoffRoute" class="geo-stop-picker mt-2">
                      <v-autocomplete
                        v-model="selectedDropoffStopNumber"
                        :items="dropoffRouteStops"
                        item-text="label"
                        item-value="stopNumber"
                        label="Dropoff Geo Stop"
                        prepend-inner-icon="mdi-map-marker-radius"
                        outlined
                        dense
                        clearable
                        :loading="loadingDropoffStops"
                        :disabled="loadingDropoffStops"
                        placeholder="Select stop on this bus..."
                        :menu-props="{ bottom: true, offsetY: true, maxHeight: 320 }"
                        :error="!!selectedDropoffRoute && !selectedDropoffStopNumber"
                        :hint="
                          selectedDropoffStopNumber
                            ? 'Used for GeoTrack'
                            : 'Required for GeoTrack sync'
                        "
                        persistent-hint
                      >
                        <template v-slot:selection="{ item }">
                          <span class="geo-stop-selection">
                            <span class="geo-stop-badge">{{ item.stopNumber }}</span>
                            <span class="geo-stop-address">{{
                              item.stopAddress || item.label
                            }}</span>
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-tile-content>
                            <v-list-tile-title class="geo-stop-item-title">
                              <span class="geo-stop-badge">{{
                                item.stopNumber
                              }}</span>
                              {{ item.stopAddress }}
                            </v-list-tile-title>
                          </v-list-tile-content>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>

            <v-flex xs12 md3 class="pl-2">
              <div
                v-if="isEditing && hasPendingLinkedRequest()"
                class="pending-request-indicator mb-2"
              >
                Last request is awaiting action.
              </div>
              <v-card outlined class="pa-2 comments-card">
                <div class="section-header mb-2">
                  <v-icon x-small class="mr-1">mdi-message-text</v-icon>
                  Messages
                </div>
                <div v-if="!isEditing" class="grey--text text--darken-1">
                  Messages are available after save.
                </div>
                <div
                  v-else-if="loadingBusChangeComments"
                  class="d-flex align-center grey--text text--darken-1"
                >
                  <v-progress-circular
                    size="18"
                    width="2"
                    indeterminate
                    color="success"
                    class="mr-2"
                  ></v-progress-circular>
                  Loading...
                </div>
                <div
                  v-else-if="busChangeComments.length === 0"
                  class="grey--text text--darken-1"
                >
                  No messages.
                </div>
                <div v-else>
                  <div
                    v-for="(comment, index) in busChangeComments"
                    :key="comment.id || comment.ID || comment.callUUID || index"
                    class="comment-entry"
                  >
                    <div class="comment-meta">
                      <span class="font-weight-medium">
                        {{ getCommentEnteredBy(comment) || "Parent" }}
                      </span>
                      <span class="mx-1">-</span>
                      <span>{{
                        formatDateTime(getCommentEnteredOn(comment))
                      }}</span>
                    </div>
                    <div
                      v-if="
                        isCommentAwaitingResponse(
                          comment,
                          index,
                          busChangeComments
                        )
                      "
                      class="comment-awaiting mt-1"
                    >
                      Action required
                    </div>
                    <div
                      v-if="getCommentCallerID(comment)"
                      class="comment-caller grey--text text--darken-1"
                    >
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ formatPhoneNumber(getCommentCallerID(comment)) }}
                    </div>
                    <div
                      v-if="getCommentText(comment)"
                      class="comment-text grey--text text--darken-3"
                    >
                      {{ getCommentText(comment) }}
                    </div>
                    <audio
                      v-if="getCommentRecordingUrl(comment)"
                      controls
                      controlsList="nodownload"
                      :src="getCommentRecordingUrl(comment)"
                      class="comment-audio mt-2"
                    ></audio>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider class="py-0"></v-divider>

        <v-card-actions class="py-3 pr-3">
          <v-btn v-if="isEditing" color="error" dark @click="openDeleteDialog">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :dark="!!selectedStudent"
            :loading="saving"
            :disabled="!selectedStudent"
            @click="saveBusChange"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Bus Change
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="560">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Delete Bus Change
        </v-card-title>
        <v-card-text class="pt-3 pb-2">
          <v-alert
            type="warning"
            outline
            value="true"
            class="delete-warning-banner mb-3"
          >
            <strong>Permanent action:</strong> deleting this bus change cannot
            be undone.
          </v-alert>
          <div v-if="recordToDelete" class="delete-summary-card">
            <div class="delete-summary-title">
              <v-icon small color="error" class="mr-1">mdi-bus-clock</v-icon>
              Bus Change Details
            </div>
            <div class="delete-summary-grid">
              <div class="delete-summary-item">
                <div class="delete-label">Student</div>
                <div class="delete-value">
                  {{ recordToDelete.studentName || "-" }}
                  <span v-if="recordToDelete.studentID">
                    ({{ recordToDelete.studentID }})
                  </span>
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Branch / Class</div>
                <div class="delete-value">
                  {{ recordToDelete.branch || "-" }} /
                  {{ recordToDelete.class || "-" }}-{{
                    recordToDelete.section || "-"
                  }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Date Range</div>
                <div class="delete-value">
                  {{ recordToDelete.startDate || "-" }} to
                  {{ recordToDelete.endDate || "Open" }}
                </div>
              </div>
              <div class="delete-summary-item">
                <div class="delete-label">Run</div>
                <div class="delete-value">
                  {{ formatPeriodLabel(recordToDelete.startPeriod) }} to
                  {{ formatPeriodLabel(recordToDelete.endPeriod) }}
                </div>
              </div>
              <div class="delete-summary-item delete-summary-item--full">
                <div class="delete-label">Address</div>
                <div class="delete-value">
                  {{ recordToDelete.busChangeAddress || "-" }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" dark :loading="deleting" @click="confirmDelete">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Grades Setup Dialog -->
    <v-dialog v-model="gradesSetupDialog" max-width="1000" persistent>
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-cog</v-icon>
          <span class="dialog-title">Bus Change Settings by Grade</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="closeGradesSetupDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Grades List View -->
        <v-card-text class="pa-3" v-if="!editingGrade">
          <v-layout row wrap class="mb-3" align-center>
            <v-flex xs12 sm3 class="pr-2">
              <v-btn-toggle
                v-model="gradesSetupSourceFilter"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="school">
                  <v-icon small class="mr-1">mdi-school</v-icon>School
                </v-btn>
                <v-btn flat small value="daycamp">
                  <v-icon small class="mr-1">mdi-tent</v-icon>Day Camp
                </v-btn>
              </v-btn-toggle>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-text-field
                v-model="gradesSetupSearch"
                placeholder="Search grades..."
                prepend-inner-icon="mdi-magnify"
                clearable
                solo
                flat
                dense
                hide-details
                background-color="#f5f5f5"
                class="compact-search"
              ></v-text-field>
            </v-flex>
            <v-flex
              xs12
              sm3
              class="pr-2"
              v-if="isAdmin() && gradesSetupSourceFilter === 'school'"
            >
              <v-autocomplete
                v-model="gradesSetupBranchFilter"
                :items="branchList"
                placeholder="All Branches"
                solo
                flat
                dense
                hide-details
                clearable
                background-color="#f5f5f5"
              ></v-autocomplete>
            </v-flex>
            <v-spacer></v-spacer>
            <v-btn
              v-if="selectedGrades.length > 0"
              small
              color="success"
              outline
              @click="openBulkExceptionDialog"
              class="mr-2"
            >
              <v-icon small left>mdi-calendar-plus</v-icon>
              Add Exception ({{ selectedGrades.length }})
            </v-btn>
            <v-btn small color="success" dark @click="openAddGrade">
              <v-icon small left>mdi-plus</v-icon>
              Add Grade
            </v-btn>
          </v-layout>

          <div class="grades-list" v-if="!loadingGradesSetup">
            <div
              v-for="grade in filteredBusChangeGradesSearched"
              :key="grade.id"
              class="grade-row"
              @click="openEditGrade(grade)"
            >
              <div class="grade-select" @click.stop>
                <v-checkbox
                  v-model="selectedGrades"
                  :value="grade.id"
                  color="success"
                  hide-details
                  class="mt-0 pt-0"
                ></v-checkbox>
              </div>
              <div class="grade-info">
                <span class="grade-name">{{ grade.grade }}</span>
                <span class="grade-branch">
                  <v-icon x-small class="mr-1">{{
                    isDayCampGrade(grade) ? "mdi-office-building-marker" : "mdi-source-branch"
                  }}</v-icon>
                  {{ isDayCampGrade(grade) ? grade.building : grade.branch }}
                </span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      x-small
                      :color="grade.allowBusChange ? '#4caf50' : '#9e9e9e'"
                      class="ml-2"
                      v-on="on"
                    >
                      {{
                        grade.allowBusChange
                          ? "mdi-check-circle"
                          : "mdi-close-circle"
                      }}
                    </v-icon>
                  </template>
                  <span>{{
                    grade.allowBusChange
                      ? "Bus change allowed"
                      : "Bus change not allowed"
                  }}</span>
                </v-tooltip>
              </div>
              <div class="grade-actions" @click.stop>
                <v-btn class="my-0" icon x-small @click="openEditGrade(grade)">
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  class="my-0"
                  icon
                  x-small
                  @click="confirmDeleteGrade(grade)"
                >
                  <v-icon x-small color="error">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div
              v-if="filteredBusChangeGradesSearched.length === 0"
              class="no-grades"
            >
              <v-icon small color="grey" class="mr-1">mdi-school</v-icon>
              <span class="grey--text caption">No grades configured</span>
            </div>
          </div>
          <div v-else class="text-center pa-4">
            <v-progress-circular
              indeterminate
              color="#4caf50"
              size="24"
            ></v-progress-circular>
          </div>
        </v-card-text>

        <!-- Edit Grade View -->
        <v-card-text class="px-3 pt-1" v-else>
          <v-btn outline @click="backToGradesList" class="mb-3 ml-n2">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to List
          </v-btn>

          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-model="currentGrade.source"
                :items="gradeSourceOptions"
                label="Source *"
                outlined
                dense
                :disabled="!!currentGrade.id"
                @change="onGradeSourceChange"
              ></v-select>
            </v-flex>
            <!-- School rules are keyed by Branch -->
            <v-flex xs12 sm3 class="pr-2" v-if="!isCurrentGradeDayCamp">
              <v-select
                v-model="currentGrade.branch"
                :items="branchList"
                label="Branch *"
                outlined
                dense
                :disabled="disableGradeSetupBranchSelection"
                @change="onGradeScopeChange"
              ></v-select>
            </v-flex>
            <!-- Day Camp rules are keyed by Building -->
            <v-flex xs12 sm3 class="pr-2" v-else>
              <v-select
                v-model="currentGrade.building"
                :items="buildingList"
                label="Building *"
                outlined
                dense
                :disabled="disableGradeSetupBuildingSelection"
                :loading="loadingBuildingList"
                @change="onGradeScopeChange"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-if="currentGrade.id"
                v-model="currentGrade.grade"
                :items="availableGrades"
                label="Grade *"
                outlined
                dense
                disabled
              ></v-select>
              <v-select
                v-else
                v-model="multipleGrades"
                :items="availableGrades"
                label="Grade(s) *"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                :disabled="!gradeScopeSelected"
                :loading="loadingAvailableGrades"
                hint="Select one or more grades"
                persistent-hint
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3>
              <v-switch
                v-model="currentGrade.allowBusChange"
                label="Allow Bus Change"
                color="success"
                hide-details
                class="mt-1"
              ></v-switch>
            </v-flex>
          </v-layout>

          <!-- Default Deadlines Section -->
          <div class="section-header mb-1">
            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
            Default Deadlines (Sun - Fri)
          </div>

          <v-layout row wrap class="mb-3">
            <v-flex
              v-for="day in weekDays"
              :key="day.value"
              xs12
              sm4
              md2
              class="pa-1"
            >
              <v-card outlined class="pa-2">
                <div class="caption font-weight-bold mb-0">{{ day.label }}</div>
                <DxDateBox
                  type="time"
                  picker-type="list"
                  :interval="15"
                  :value="getDefaultDeadlineAsDate(day.value)"
                  @value-changed="
                    (e) => setDefaultDeadlineFromDate(day.value, e.value)
                  "
                  placeholder="Not set"
                  class="deadline-time-picker"
                />
                <v-menu
                  offset-y
                  v-if="getDaysWithDeadlines(day.value).length > 0"
                >
                  <template v-slot:activator="{ on }">
                    <span class="copy-from-link" v-on="on">
                      <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                      Copy from
                    </span>
                  </template>
                  <v-list dense>
                    <v-list-tile
                      v-for="sourceDay in getDaysWithDeadlines(day.value)"
                      :key="sourceDay.value"
                      @click="copyDeadlineFromDay(sourceDay.value, day.value)"
                    >
                      <v-list-tile-title>
                        {{ sourceDay.label }} ({{
                          formatTimeDisplay(
                            getDefaultDeadline(sourceDay.value)
                          )
                        }})
                      </v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-card>
            </v-flex>
          </v-layout>

          <!-- Exception Deadlines Section -->
          <div class="section-header mb-2 mt-4">
            <v-icon small class="mr-1">mdi-calendar</v-icon>
            Exception Deadlines
            <v-spacer></v-spacer>
            <v-btn small color="success" dark @click="addException">
              <v-icon x-small left>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>

          <div class="exceptions-container" v-if="currentExceptions.length > 0">
            <div
              v-for="(exception, index) in currentExceptions.filter(
                (e) => !e._deleted
              )"
              :key="index"
              class="exception-chip"
            >
              <div class="exception-content">
                <v-icon small color="rgb(92, 184, 96)" class="mr-1"
                  >mdi-calendar</v-icon
                >
                <span class="exception-date">{{
                  formatExceptionDateShort(exception.exceptionDate)
                }}</span>
                <span class="exception-divider">•</span>
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                <span class="exception-time">{{
                  formatTimeDisplay(exception.deadline)
                }}</span>
              </div>
              <div class="exception-actions">
                <v-btn
                  icon
                  x-small
                  @click="editException(exception)"
                  class="action-btn"
                >
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click="removeException(exception)"
                  class="action-btn delete-btn"
                >
                  <v-icon x-small color="error">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div v-else class="no-exceptions">
            <v-icon small color="grey" class="mr-1">mdi-calendar-blank</v-icon>
            <span class="grey--text caption">No exceptions configured</span>
          </div>

          <div v-if="showUnsavedWarning" class="unsaved-warning mt-3">
            <v-icon x-small color="orange" class="mr-1">mdi-alert</v-icon>
            <span>You have unsaved changes. Click "Save Grade" to apply.</span>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3" v-if="editingGrade">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="backToGradesList">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="savingGradesSetup"
            @click="saveCurrentGrade"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Grade
          </v-btn>
        </v-card-actions>
        <v-card-actions class="pa-3" v-else>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeGradesSetupDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Exception Dialog -->
    <v-dialog v-model="exceptionDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ editingException ? "Edit Exception" : "Add Exception" }}
        </v-card-title>
        <v-card-text>
          <v-menu
            v-model="exceptionDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="newException.exceptionDate"
                label="Date *"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="newException.exceptionDate"
              color="rgb(92, 184, 96)"
              @input="exceptionDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <div class="mb-3">
            <div class="caption mb-1">Deadline Time *</div>
            <DxDateBox
              type="time"
              picker-type="list"
              :interval="15"
              :value="getExceptionDeadlineAsDate()"
              @value-changed="setExceptionDeadlineFromDate"
              placeholder="Select time"
              class="deadline-time-picker"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="exceptionDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" dark @click="saveException">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Grade Confirmation -->
    <v-dialog v-model="deleteGradeDialog" max-width="400">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Delete Grade
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            Are you sure you want to delete this grade? This will also delete
            all associated deadlines.
          </v-alert>
          <div class="mt-3 subheading-1" v-if="gradeToDelete">
            <span class="font-weight-medium">Grade:</span>
            {{ gradeToDelete.grade }} ({{
              isDayCampGrade(gradeToDelete)
                ? gradeToDelete.building
                : gradeToDelete.branch
            }})
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteGradeDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            dark
            :loading="deletingGrade"
            @click="deleteGrade"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Add Exception Dialog -->
    <v-dialog v-model="bulkExceptionDialog" max-width="450">
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left dark>mdi-calendar-plus</v-icon>
          Add Exception to Selected Grades
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3">
            <div class="caption grey--text mb-2">
              Selected Grades ({{ selectedGrades.length }})
            </div>
            <div class="selected-grades-list">
              <div
                v-for="grade in getSelectedGradeDetails()"
                :key="grade.id"
                class="selected-grade-item"
              >
                <span class="grade-text">
                  <strong>{{ grade.grade }}</strong>
                  <span class="grey--text ml-2"
                    >({{
                      isDayCampGrade(grade) ? grade.building : grade.branch
                    }})</span
                  >
                </span>
              </div>
            </div>
          </div>
          <v-menu
            v-model="bulkExceptionDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="bulkException.exceptionDate"
                label="Date *"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="bulkException.exceptionDate"
              color="rgb(92, 184, 96)"
              @input="bulkExceptionDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <div class="mb-3">
            <div class="caption mb-1">Deadline Time *</div>
            <DxDateBox
              type="time"
              picker-type="list"
              :interval="15"
              :value="getBulkExceptionDeadlineAsDate()"
              @value-changed="setBulkExceptionDeadlineFromDate"
              placeholder="Select time"
              class="deadline-time-picker"
            />
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="bulkExceptionDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            dark
            :loading="savingBulkException"
            @click="saveBulkException"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Exception
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="800">
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text"
            icon
            dark
            @click="closeAccountSearchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Phone Number Search -->
          <v-layout row wrap class="mb-1">
            <v-flex xs12 class="pr-2">
              <v-text-field
                v-model="dialogPhoneSearch"
                label="Search by Phone Number"
                prepend-inner-icon="mdi-phone"
                outlined
                dense
                clearable
                mask="###-###-####"
                @input="searchDialogByPhone"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Search Name Mode Toggle -->
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 class="pr-2">
              <div class="caption grey--text mb-1">Or Search By Name</div>
              <v-btn-toggle
                v-model="dialogSearchNameMode"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="yiddish">אידיש</v-btn>
                <v-btn flat small value="english">English</v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>

          <!-- Account Search Fields -->
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogLastNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'לעצטע נאמען'
                    : 'Last Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogFirstNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'טאטעס נאמען'
                    : 'Father Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <div class="caption grey--text mb-1">Or Search By Address</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogHouseNumberSearch"
                label="House Number"
                prepend-inner-icon="mdi-home"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogStreetSearch"
                label="Street Name"
                prepend-inner-icon="mdi-road"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Account List -->
          <div class="account-list-container" v-if="dialogAccounts.length > 0">
            <div class="caption grey--text mb-2 px-2">
              Select an account ({{ dialogAccounts.length }} found)
            </div>
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in dialogAccounts"
                :key="account.accountID"
                @click="onAccountSelected(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span class="grey--text ml-2">({{ account.hname }})</span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    Account ID: {{ account.accountID }}
                    <span v-if="account.homePhone" class="ml-1">
                      | {{ account.homePhone }}
                    </span>
                    <span v-if="account.cellPhone" class="ml-1">
                      | {{ account.cellPhone }}
                    </span>
                    <span v-if="account.address1" class="ml-1">
                      | {{ account.address1 }}
                    </span>
                    <span v-if="account.busAddress1" class="ml-1">
                      | Bus: {{ account.busAddress1 }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>

          <!-- No Accounts Message -->
          <v-alert
            v-if="dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            type="warning"
            outline
            dense
            value="true"
            class="mb-3"
          >
            No accounts found. Please try a different search.
          </v-alert>

          <!-- Empty state -->
          <div
            v-if="!dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            class="text-center px-2 pb-3 grey--text"
          >
            <v-icon large color="grey lighten-1">mdi-account-search</v-icon>
            <div class="mt-2">
              Search for an account by phone, name, or address
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Day Camp Enrollment Warning Dialog -->
    <v-dialog v-model="enrollmentWarningDialog" max-width="520">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-tent</v-icon>
          Outside Day Camp Enrollment
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ enrollmentDateWarning }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="cancelEnrollmentWarning"
            >Cancel</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithEnrollmentWarning">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Incomplete Route Confirmation Dialog -->
    <v-dialog v-model="incompleteRouteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Incomplete Route Selection
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ getIncompleteRouteMessage() }}
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="incompleteRouteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithIncompleteRoute">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Geo Stop Confirmation Dialog -->
    <v-dialog v-model="missingGeoStopDialog" max-width="520">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-map-marker-off</v-icon>
          Geo Stop Not Selected
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            {{ missingGeoStopMessage }}
          </v-alert>
          <div class="mt-3">
            GeoTrack will not sync for routes without a stop. Continue saving
            anyway?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="missingGeoStopDialog = false"
            >Go Back</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithoutGeoStop">
            Continue Without Geo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Confirmation Dialog -->
    <v-dialog v-model="notificationConfirmDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-information</v-icon>
          Notification Will Be Sent
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert value="true" type="info" outline class="mb-3">
            <div class="font-weight-bold mb-1">
              <v-icon small color="info" class="mr-1">
                {{
                  newBusChange.notificationType === "CALL"
                    ? "mdi-phone"
                    : "mdi-message-text"
                }}
              </v-icon>
              {{
                newBusChange.notificationType === "CALL"
                  ? "A phone call"
                  : "A text message"
              }}
              will automatically be sent to:
            </div>
            <div class="ml-4">
              {{ formatPhoneNumber(newBusChange.notificationPhoneNumber) }}
            </div>
          </v-alert>
          <div class="grey--text text--darken-1">
            The bus change will be saved and the notification will be sent
            automatically.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-btn
            v-if="!isSourceMandatory"
            color="success"
            outline
            @click="proceedWithoutNotification"
          >
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="proceedWithNotification">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Notification Fields Dialog -->
    <v-dialog
      v-model="missingNotificationFieldsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Missing Notification Information
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" class="mb-3">
            <div class="font-weight-bold mb-2">
              Cannot send notification - missing required fields:
            </div>
            <ul class="ml-4">
              <li v-if="!newBusChange.notificationPhoneNumber">
                Notification Phone Number
              </li>
              <li v-if="!newBusChange.notificationType">
                Notification Type
              </li>
            </ul>
          </v-alert>
          <div class="mt-3">
            Would you like to fill in these fields or save without sending a
            notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-btn color="warning" @click="saveWithoutNotification">
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="closeMissingFieldsDialog">
            Fill in Fields
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cleared Notification Warning Dialog (IVR/ONLINE) -->
    <v-dialog
      v-model="clearedNotificationWarningDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Notification Fields Cleared
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" outlined class="mb-3">
            <div class="font-weight-bold mb-2 grey--text text--darken-4">
              This record originally had notification information
            </div>
            <div class="grey--text text--darken-3">
              By clearing the notification fields, the user will NOT receive a
              notification about this bus change.
            </div>
          </v-alert>
          <div class="mt-3">
            Do you want to continue saving without notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelClearedNotificationWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <bus-change-report-dialog ref="busChangeReportDialog" />
  </div>
</template>

<script>
import moment from "moment";
import DxDateBox from "devextreme-vue/date-box";
import { normalizeRecordingUrl } from "../../utils/helpers.js";
import { getBusChangeNotificationNotices } from "./busChangeNotification.js";
import BusChangeReportDialog from "./BusChangeReportDialog.vue";

export default {
  components: {
    DxDateBox,
    BusChangeReportDialog,
  },
  data: () => ({
    title: "Bus Changes",
    actions: [],
    url: "",

    // Dialog state
    addDialog: false,
    saving: false,
    loadingStudents: false,
    isEditing: false,
    editingId: null,
    editingRecordInfo: null,
    editingExtraInfo: false,
    savingExtraInfo: false,
    extraInfoBackup: null,
    busChangeComments: [],
    loadingBusChangeComments: false,

    // Account search
    phoneSearch: "",
    firstNameSearch: "",
    lastNameSearch: "",
    houseNumberSearch: "",
    streetSearch: "",
    accounts: [],
    selectedAccount: null,
    searchNameMode: "yiddish",
    showNoAccountsMessage: false,
    searchDebounceTimer: null,

    // Student selection
    students: [],
    selectedStudent: null,
    branchList: [],

    // Student source (school vs day camp enrollment)
    studentSource: "school",
    summerDates: null, // { startDate, endDate } from day camp season
    loadingStudentsError: false,
    enrollmentWarningDialog: false,
    skipEnrollmentWarning: false,

    // Grades setup
    gradeError: null,
    selectedGradeDeadlines: [], // Cached deadlines for selected student's grade
    gradesSetupDialog: false,
    gradesSetupSearch: "",
    gradesSetupBranchFilter: null,
    gradesSetupSourceFilter: "school",
    gradeSourceOptions: [
      { text: "School", value: "school" },
      { text: "Day Camp", value: "daycamp" },
    ],
    buildingList: [],
    loadingBuildingList: false,
    userDayCampBuildings: [],
    loadingGradesSetup: false,
    savingGradesSetup: false,
    busChangeGrades: [],
    editingGrade: false,
    currentGrade: {
      id: null,
      grade: "",
      branch: "",
      building: "",
      source: "school",
      allowBusChange: true,
    },
    currentDeadlines: [],
    currentExceptions: [],
    selectedGrades: [], // For bulk exception
    bulkExceptionDialog: false,
    bulkExceptionDateMenu: false,
    savingBulkException: false,
    bulkException: { exceptionDate: "", deadline: "" },
    gradesSetupHeaders: [
      { text: "Grade", value: "grade", sortable: true },
      { text: "Branch", value: "branch", sortable: true },
      {
        text: "Allow Bus Change",
        value: "allowBusChange",
        sortable: false,
        align: "center",
      },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
        width: "120px",
      },
    ],
    exceptionHeaders: [
      { text: "Date", value: "exceptionDate", sortable: true },
      { text: "Deadline", value: "deadline", sortable: false },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
        width: "100px",
      },
    ],
    weekDays: [
      { value: 0, label: "Sunday" },
      { value: 1, label: "Monday" },
      { value: 2, label: "Tuesday" },
      { value: 3, label: "Wednesday" },
      { value: 4, label: "Thursday" },
      { value: 5, label: "Friday" },
    ],
    exceptionDialog: false,
    exceptionDateMenu: false,
    editingException: null,
    newException: { exceptionDate: "", deadline: "" },
    showUnsavedWarning: false,
    deleteGradeDialog: false,
    deletingGrade: false,
    gradeToDelete: null,
    availableGrades: [],
    loadingAvailableGrades: false,
    multipleGrades: [], // For adding multiple grades at once

    // Bus Routes selection
    busRoutes: [],
    pickupRoutes: [],
    dropoffRoutes: [],
    selectedPickupRoute: null,
    selectedDropoffRoute: null,
    loadingBusRoutes: false,

    // Delete dialog
    deleteDialog: false,
    deleting: false,
    recordToDelete: null,

    // Account search dialog
    accountSearchDialog: false,
    dialogPhoneSearch: "",
    dialogFirstNameSearch: "",
    dialogLastNameSearch: "",
    dialogHouseNumberSearch: "",
    dialogStreetSearch: "",
    dialogAccounts: [],
    dialogSearchNameMode: "yiddish",
    dialogShowNoAccountsMessage: false,
    dialogSearchDebounceTimer: null,

    // Incomplete route confirmation dialog
    incompleteRouteDialog: false,
    skipRouteWarning: false,
    missingGeoStopDialog: false,
    skipGeoStopWarning: false,
    missingGeoStopMessage: "",
    selectedPickupStopNumber: null,
    selectedDropoffStopNumber: null,
    pickupRouteStops: [],
    dropoffRouteStops: [],
    loadingPickupStops: false,
    loadingDropoffStops: false,

    // Bus change notification dialogs and state
    notificationConfirmDialog: false,
    missingNotificationFieldsDialog: false,
    clearedNotificationWarningDialog: false,
    skipNotification: false,
    originalPickupRouteID: null,
    originalDropoffRouteID: null,
    originalNotificationPhone: null,
    originalNotificationType: null,
    savedBusChangeId: null,
    sendingNotification: false,

    // Track if start date was edited
    startDateEdited: false,

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,

    showCompletedGridBusChanges: false,
    showDeletedGridBusChanges: false,

    // New bus change data
    newBusChange: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      notificationPhoneNumber: "",
      notificationType: "",
      note: "",
    },
    periodOptions: [
      { label: "Morning Pickup", value: "AM" },
      { label: "Afternoon Dropoff", value: "PM" },
    ],

    rules: {
      required: (value) => !!value || "Required.",
      phoneNumber: (v) => {
        if (!v) return true;
        const digits = v.replace(/[-\s()]/g, "");
        return (
          ([10, 11].includes(digits.length) && /^\d+$/.test(digits)) ||
          "Invalid Phone Number"
        );
      },
    },

    columns: [
      {
        field: "statusIcon",
        label: "Status",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        icon: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "NEW":
              return "mdi-new-box";
            case "FUTURE":
            case "PENDING":
              return "mdi-clock-outline";
            case "ACTIVE":
              return "mdi-check-circle";
            case "COMPLETED":
              return "mdi-check-all";
            case "DELETED":
              return "mdi-delete";
            default:
              return "";
          }
        },
        iconColor: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "NEW":
              return "#2196f3";
            case "FUTURE":
            case "PENDING":
              return "#ff9800";
            case "ACTIVE":
              return "rgb(92, 184, 96)";
            case "COMPLETED":
              return "#9e9e9e";
            case "DELETED":
              return "#f44336";
            default:
              return "";
          }
        },
      },
      {
        label: "Student ID",
        field: "studentID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name",
        field: "studentName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name (Heb)",
        field: "studentNameHeb",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Family ID",
        field: "familyID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building",
        field: "building",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Branch",
        field: "branch",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Class",
        field: "class",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Section",
        field: "section",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Term",
        field: "term",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Start Date",
        field: "startDate",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 1,
        sortOrder: "desc",
      },
      {
        label: "Start Period",
        field: "startPeriod",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "End Date",
        field: "endDate",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "End Period",
        field: "endPeriod",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Address",
        field: "homeAddress",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Address",
        field: "busChangeAddress",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Description",
        field: "busChangeDescription",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Phone",
        field: "busChangePhoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Geo Phone Number",
        field: "geoPhoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Pickup Route ID",
        field: "pickupRouteID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Dropoff Route ID",
        field: "dropoffRouteID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Note",
        field: "note",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Name",
        field: "busChangeName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Account ID",
        field: "busChangeAccountID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Phone",
        field: "notificationPhoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Type",
        field: "notificationType",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 0,
        sortOrder: "asc",
        calculateSortValue: (data) => {
          const order = {
            NEW: 1,
            ACTIVE: 2,
            FUTURE: 3,
            PENDING: 3,
            COMPLETED: 4,
            DELETED: 5,
          };
          const status = data && data.status ? data.status.toUpperCase() : "";
          return order[status] || 6;
        },
      },
      {
        label: "Father Name",
        field: "fatherName",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Father Name (Heb)",
        field: "fatherNameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Email",
        field: "emailAddress",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Source",
        field: "source",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Entered By",
        field: "enteredBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Entered On",
        field: "enteredOn",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),

  computed: {
    permittedBranches() {
      if (!this.userBranch) return [];
      return this.userBranch
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean);
    },
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    disableGradeSetupBranchSelection() {
      if (this.currentGrade.id) return true;
      if (this.isAdmin()) return false;
      return this.permittedBranches.length <= 1;
    },
    disableGradeSetupBuildingSelection() {
      if (this.currentGrade.id) return true;
      if (this.isAdmin()) return false;
      return (
        this.hasUserDayCampBuildings() && this.userDayCampBuildings.length <= 1
      );
    },
    deadlineWarningMessage() {
      if (!this.selectedStudent) return null;
      if (!this.startDateEdited) return null;
      return this.checkDeadlineWarning(
        this.selectedStudent.class,
        this.selectedStudent.branch,
        this.newBusChange.startDate
      );
    },
    isDayCamp() {
      return this.studentSource === "daycamp";
    },
    isCurrentGradeDayCamp() {
      return this.currentGrade.source === "daycamp";
    },
    gradeScopeSelected() {
      // The "scope" is the field a grade rule is keyed by: Branch (school) or Building (day camp).
      return this.isCurrentGradeDayCamp
        ? !!this.currentGrade.building
        : !!this.currentGrade.branch;
    },
    enrollmentDateWarning() {
      return this.getEnrollmentDateWarning();
    },
    filteredBusChangeGrades() {
      const wantDayCamp = this.gradesSetupSourceFilter === "daycamp";
      let grades = this.busChangeGrades.filter(
        (g) => this.isDayCampGrade(g) === wantDayCamp
      );

      if (wantDayCamp) {
        // Day Camp rules are keyed by Building — restrict non-admins with DayCampBuilding attributes.
        if (this.hasUserDayCampBuildings()) {
          grades = grades.filter((g) =>
            this.userDayCampBuildings.includes(
              g.building ? g.building.trim() : ""
            )
          );
        }
      } else if (!this.isAdmin() && this.userBranch) {
        // Handle comma-separated branches like "2411,2412"
        const allowedBranches = this.userBranch
          .split(",")
          .map((b) => b.trim());
        grades = grades.filter((g) => allowedBranches.includes(g.branch));
      } else if (this.gradesSetupBranchFilter) {
        grades = grades.filter(
          (g) => g.branch === this.gradesSetupBranchFilter
        );
      }
      return grades;
    },
    filteredBusChangeGradesSearched() {
      let grades = this.filteredBusChangeGrades;
      if (this.gradesSetupSearch) {
        const search = this.gradesSetupSearch.toLowerCase();
        grades = grades.filter(
          (g) =>
            (g.grade && g.grade.toLowerCase().includes(search)) ||
            (g.branch && g.branch.toLowerCase().includes(search)) ||
            (g.building && g.building.toLowerCase().includes(search))
        );
      }
      return grades;
    },
    summerSeasonName() {
      const year =
        this.summerDates && this.summerDates.startDate
          ? moment(this.summerDates.startDate).year()
          : moment().year();
      return `Summer ${year}`;
    },
    filteredPickupRoutes() {
      let routes = this.pickupRoutes;

      // Filter by selected student's building
      if (this.selectedStudent && this.selectedStudent.building) {
        const studentBuilding = this.selectedStudent.building.toString();
        routes = routes.filter((r) => {
          if (!r.buildings) return false;
          // buildings can be comma-separated like "75,212"
          const routeBuildings = r.buildings.split(",").map((b) => b.trim());
          return routeBuildings.includes(studentBuilding);
        });
      }

      return this.filterRoutesBySeason(routes);
    },
    filteredDropoffRoutes() {
      let routes = this.dropoffRoutes;

      // Filter by selected student's building
      if (this.selectedStudent && this.selectedStudent.building) {
        const studentBuilding = this.selectedStudent.building.toString();
        routes = routes.filter((r) => {
          if (!r.buildings) return false;
          // buildings can be comma-separated like "75,212"
          const routeBuildings = r.buildings.split(",").map((b) => b.trim());
          return routeBuildings.includes(studentBuilding);
        });
      }

      return this.filterRoutesBySeason(routes);
    },
    isSourceMandatory() {
      // Check if the source requires mandatory notification
      if (!this.editingRecordInfo || !this.editingRecordInfo.source) {
        return false;
      }
      const source = this.editingRecordInfo.source.toUpperCase();
      return source === "IVR" || source === "ONLINE";
    },
  },

  watch: {
    searchNameMode() {
      // Re-search when switching between English and Yiddish
      if (this.firstNameSearch || this.lastNameSearch) {
        this.searchByName();
      }
    },
    dialogSearchNameMode() {
      // Re-search when switching between English and Yiddish in dialog
      if (this.dialogFirstNameSearch || this.dialogLastNameSearch) {
        this.searchDialogByName();
      }
    },
  },

  mounted() {
    // Opened from Bus Change Requests simple view: /bus-changes?add=1
    if (this.$route.query && this.$route.query.add === "1") {
      this.openAddDialog();
      this.$router.replace({ name: "BusChanges", query: {} });
    }
  },

  methods: {
    buildBusChangeGridUrl() {
      // Backend: building takes precedence; branch is only used when no building filter is sent.
      // DayCampBuilding only applies during the summer season; after summer use branch as usual.
      // OData DxGrid: always include every query param key, even when the value is empty.
      const useBuilding = this.useDayCampBuildingFilter();
      const branchValue =
        !useBuilding && this.userBranch ? this.userBranch : "";
      const buildingValue = useBuilding
        ? this.userDayCampBuildings.join(",")
        : "";
      const includeCompleted = this.showCompletedGridBusChanges
        ? "true"
        : "false";
      const includeDeleted = this.showDeletedGridBusChanges ? "true" : "false";
      return (
        "busChange?branch=" +
        branchValue +
        "&building=" +
        buildingValue +
        "&includeCompleted=" +
        includeCompleted +
        "&includeDeleted=" +
        includeDeleted
      );
    },

    reloadBusChangeGrid() {
      this.url = this.buildBusChangeGridUrl();
      this.$refs.grid.reloadDataSource(this.url);
    },

    async beforeSetupGrid() {
      this.showCompletedGridBusChanges = false;
      this.showDeletedGridBusChanges = false;
      await Promise.all([
        this.loadUserDayCampBuildings(),
        this.loadSummerDates(),
      ]);
      this.url = this.buildBusChangeGridUrl();

      this.actions.push({
        text: "Add Bus Change",
        action: this.openAddDialog,
        icon: "mdi-plus",
      });

      this.actions.push({
        text: "Grades Setup",
        action: this.openGradesSetupDialog,
        icon: "mdi-cog",
      });

      this.actions.push({
        text: "Show Completed",
        type: "switch",
        align: "left",
        action: this.onShowCompletedChanged,
      });

      if (this.isAdmin()) {
        this.actions.push({
          text: "Show Deleted",
          type: "switch",
          align: "left",
          action: this.onShowDeletedChanged,
        });
      }

      this.actions.push({
        text: "Download Report",
        action: this.openReportDialog,
        icon: "mdi-download",
      });
    },

    openReportDialog() {
      if (this.$refs.busChangeReportDialog) {
        this.$refs.busChangeReportDialog.open();
      }
    },

    onShowCompletedChanged(value) {
      this.showCompletedGridBusChanges = !!value;
      this.reloadBusChangeGrid();
    },

    onShowDeletedChanged(value) {
      this.showDeletedGridBusChanges = !!value;
      this.reloadBusChangeGrid();
    },

    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },

    async loadUserDayCampBuildings() {
      const username = this.$store.state.authentication.user;

      if (!username) {
        this.userDayCampBuildings = [];
        return;
      }

      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributesByUsername?username=" +
            encodeURIComponent(username)
        );

        this.userDayCampBuildings = (attributes || [])
          .filter((a) => a && a.name && a.name.trim() === "DayCampBuilding")
          .map((a) => (a.value ? a.value.trim() : ""))
          .filter(Boolean);
      } catch (e) {
        this.userDayCampBuildings = [];
      }
    },

    hasUserDayCampBuildings() {
      return !this.isAdmin() && this.userDayCampBuildings.length > 0;
    },

    // Grid/report scoping: DayCampBuilding only while summer season is active.
    useDayCampBuildingFilter() {
      return this.hasUserDayCampBuildings() && this.isWithinSummerSeason();
    },

    customRouteFilter(item, queryText, itemText) {
      // Vuetify may call this with autocomplete as `this` — keep self-contained.
      if (!queryText) return true;
      const searchUpper = String(queryText).toUpperCase().trim();
      const route = item && typeof item === "object" ? item : null;
      if (!route) {
        return String(itemText || item || "")
          .toUpperCase()
          .includes(searchUpper);
      }
      const fields = [
        route.description,
        route.busNumber,
        route.actualBusNumber,
        route.ActualBusNumber,
        route.BusNumber,
        route.driver,
        route.color,
        route.timeStart,
        route.routeIdentifier,
        itemText,
      ];
      return fields.some(
        (f) => f != null && String(f).toUpperCase().includes(searchUpper)
      );
    },

    getRouteBusNumber(route) {
      // Prefer actualBusNumber from GetBusRoutes when present; else busNumber
      if (!route) return "";
      const actual = (route.actualBusNumber || "").toString().trim();
      if (actual) return actual;
      return (route.busNumber || "").toString().trim();
    },

    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860"; // Green default instead of white
    },

    getRouteChipStyle(route) {
      // Default style when no route or no color
      if (!route || !route.color) {
        return {
          background: "#5cb860", // Green default
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const bgColor = this.getRouteColor(route.color);
      if (!bgColor) {
        return {
          background: "#5cb860", // Green default
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      // Determine if we need light or dark text based on background
      const colorUpper = route.color.toUpperCase().trim();
      const isLightBackground = [
        "BEIGE",
        "WHITE",
        "YELLOW",
        "LIGHT BLUE",
        "LIGHT GREEN",
        "LIGHT PINK",
        "LIGHT PURPLE",
        "SILVER",
        "GOLD",
        "PINK",
        "ORANGE",
        "LAVENDER",
        "PEACH",
        "MINT",
      ].includes(colorUpper);

      // For white or very light backgrounds, use a more visible style
      if (
        colorUpper === "WHITE" ||
        bgColor.toUpperCase() === "#FFF" ||
        bgColor.toUpperCase() === "#FFFFFF"
      ) {
        return {
          background: "#f5f5f5",
          color: "#000000",
          border: "2px solid #cccccc",
        };
      }

      return {
        background: bgColor,
        color: isLightBackground ? "#000000" : "#FFFFFF",
        border: isLightBackground
          ? "1.5px solid rgba(0, 0, 0, 0.3)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      };
    },

    async openAddDialog() {
      this.addDialog = true;
      this.resetForm();
      // Determine the season/source first so bus routes load filtered by the
      // summer season when defaulting to day camp mode.
      await this.loadSummerDates();
      this.studentSource = this.getDefaultStudentSource();
      await Promise.all([
        this.loadBranches(),
        this.loadBusChangeGrades(),
        this.loadBusRoutes(),
      ]);
    },

    async onRowClicked(row) {
      this.resetForm();

      // Load bus change grades and bus routes if not loaded
      const loadPromises = [];
      if (!this.busChangeGrades.length) {
        loadPromises.push(this.loadBusChangeGrades());
      }
      if (!this.busRoutes.length) {
        loadPromises.push(this.loadBusRoutes());
      }
      if (loadPromises.length) {
        await Promise.all(loadPromises);
      }

      this.isEditing = true;
      this.editingId = row.id;

      // Set record info for display
      this.editingRecordInfo = {
        source: row.source,
        enteredBy: row.enteredBy,
        enteredOn: row.enteredOn,
        recordingURL: row.recordingURL || null,
        pendingBusChangeRequest: false,
        statusRevertedToPendingOn: null,
        homePhone: row.homePhone || null,
        cellPhone: row.cellPhone || null,
        callerID: null,
        busChangeRequestID:
          row.busChangeRequestID ||
          row.busChangeRequestId ||
          row.requestID ||
          row.requestId ||
          null,
      };

      const linkedRequest = await this.loadLinkedRequestForBusChange(row.id);
      if (linkedRequest) {
        this.editingRecordInfo.busChangeRequestID =
          linkedRequest.id ||
          linkedRequest.busChangeRequestID ||
          linkedRequest.busChangeRequestId ||
          this.editingRecordInfo.busChangeRequestID;
        this.editingRecordInfo.pendingBusChangeRequest = this.isPendingStatus(
          linkedRequest.status || linkedRequest.Status || ""
        );
        this.editingRecordInfo.statusRevertedToPendingOn =
          linkedRequest.statusRevertedToPendingOn ||
          linkedRequest.StatusRevertedToPendingOn ||
          null;
        this.editingRecordInfo.callerID =
          linkedRequest.callerID || linkedRequest.CallerID || null;
        if (!this.editingRecordInfo.recordingURL) {
          this.editingRecordInfo.recordingURL =
            linkedRequest.recordingURL ||
            linkedRequest.RecordingURL ||
            linkedRequest.recordingUrl ||
            null;
        }
      }

      await this.loadBusChangeComments(
        this.editingRecordInfo.busChangeRequestID
      );

      // Set selected student info from the row data
      this.selectedStudent = {
        studentID: row.studentID,
        studentName: row.studentName,
        hebName: row.studentNameHeb,
        familyID: row.familyID || row.FamilyID || "",
        building: row.building,
        branch: row.branch,
        class: row.class,
        section: row.section,
        address1: row.homeAddress,
        regID: row.term,
        teacherH: row.teacherH,
        teacherH2: row.teacherH2,
        room: "",
        isDayCamp: false,
        regType: "",
        weeks: [],
        enrollmentStartDate: null,
        enrollmentEndDate: null,
      };

      // Fetch full student info to get teacher details
      if (row.studentID) {
        try {
          const studentInfo = await this.$http.get(
            `api/students/getStudent?studentID=${row.studentID}`
          );
          if (studentInfo) {
            this.selectedStudent.teacherH =
              studentInfo.teacherH || row.teacherH;
            this.selectedStudent.teacherH2 =
              studentInfo.teacherH2 || row.teacherH2;
          }
        } catch (e) {
          // Keep the values from the row if API call fails
        }
      }

      // Set form data from the row
      this.newBusChange = {
        startDate: row.startDate ? this.formatDateForPicker(row.startDate) : "",
        endDate: row.endDate ? this.formatDateForPicker(row.endDate) : "",
        startPeriod: row.startPeriod || row.StartPeriod || "",
        endPeriod: row.endPeriod || row.EndPeriod || "",
        busChangeAddress: row.busChangeAddress || "",
        busChangePhoneNumber: row.busChangePhoneNumber || "",
        geoPhoneNumber: row.geoPhoneNumber || "",
        busChangeName: row.busChangeName || "",
        busChangeAccountID: row.busChangeAccountID || "",
        notificationPhoneNumber: row.notificationPhoneNumber || "",
        notificationType: row.notificationType || "",
        note: row.note || "",
      };

      // If this bus change falls within the day camp season, load and show the
      // student's day camp class/enrollment info (same as when creating).
      await this.loadDayCampInfoForEdit(row);

      // Set selected pickup and dropoff routes if they exist
      let pickupStopNumber =
        row.pickupStopNumber != null ? row.pickupStopNumber : null;
      let dropoffStopNumber =
        row.dropoffStopNumber != null ? row.dropoffStopNumber : null;

      // Prefer stop numbers from BusChanges entity (view may not have columns yet)
      try {
        const detail = await this.$http.get(
          `api/buschange/getById?id=${row.id}`
        );
        if (detail) {
          if (detail.pickupStopNumber != null) {
            pickupStopNumber = detail.pickupStopNumber;
          }
          if (detail.dropoffStopNumber != null) {
            dropoffStopNumber = detail.dropoffStopNumber;
          }
        }
      } catch (e) {
        // keep values from row
      }

      if (row.pickupRouteID) {
        const pickupRoute = this.busRoutes.find(
          (r) => r.id === row.pickupRouteID
        );
        this.selectedPickupRoute = pickupRoute || null;
        if (pickupRoute) {
          await this.loadRouteStops(
            "pickup",
            pickupRoute.id,
            pickupStopNumber
          );
        }
      }
      if (row.dropoffRouteID) {
        const dropoffRoute = this.busRoutes.find(
          (r) => r.id === row.dropoffRouteID
        );
        this.selectedDropoffRoute = dropoffRoute || null;
        if (dropoffRoute) {
          await this.loadRouteStops(
            "dropoff",
            dropoffRoute.id,
            dropoffStopNumber
          );
        }
      }

      // Track original route IDs for notification detection
      this.originalPickupRouteID = row.pickupRouteID || null;
      this.originalDropoffRouteID = row.dropoffRouteID || null;

      // Track original notification fields for IVR/ONLINE validation
      this.originalNotificationPhone = row.notificationPhoneNumber || null;
      this.originalNotificationType = row.notificationType || null;

      // Load deadlines for deadline warnings
      await this.loadSelectedGradeDeadlines(row.class, row.branch);

      this.addDialog = true;
    },

    formatDateForPicker(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatDateTime(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("MM/DD/YYYY hh:mm A");
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "";
      // Remove all non-digit characters
      const cleaned = phoneNumber.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      // Return original if not 10 digits
      return phoneNumber;
    },

    getPeriodOrder(period) {
      if (period === "PM") return 2;
      return 1;
    },

    formatPeriodLabel(period) {
      if (!period) return "-";
      const normalized = period
        .toString()
        .trim()
        .toUpperCase();
      if (normalized === "AM") return "Morning Pickup";
      if (normalized === "PM") return "Afternoon Dropoff";
      return period;
    },

    isPendingStatus(status) {
      if (!status) return false;
      return status.toString().trim().toUpperCase() === "PENDING";
    },

    hasPendingLinkedRequest() {
      return (
        this.editingRecordInfo &&
        this.editingRecordInfo.busChangeRequestID &&
        this.editingRecordInfo.pendingBusChangeRequest
      );
    },

    getPendingRevertedToDate() {
      if (!this.editingRecordInfo) return null;
      return this.editingRecordInfo.statusRevertedToPendingOn || null;
    },

    isCommentAwaitingResponse(comment, index, comments) {
      if (!this.hasPendingLinkedRequest()) return false;
      const revertedToPendingOn = this.getPendingRevertedToDate();

      if (revertedToPendingOn) {
        const enteredOn = this.getCommentEnteredOn(comment);
        if (!enteredOn) return false;
        return moment(enteredOn).isSameOrAfter(moment(revertedToPendingOn));
      }

      return comments && index === comments.length - 1;
    },

    getIncompleteRouteMessage() {
      const hasPickup =
        this.selectedPickupRoute && this.selectedPickupRoute.id;
      const hasDropoff =
        this.selectedDropoffRoute && this.selectedDropoffRoute.id;

      if (hasPickup && !hasDropoff) {
        return "You have only selected the Pickup route.";
      }
      if (!hasPickup && hasDropoff) {
        return "You have only selected the Dropoff route.";
      }
      return "You have not selected any route.";
    },

    isEndPeriodBeforeStartPeriod(startPeriod, endPeriod) {
      return this.getPeriodOrder(endPeriod) < this.getPeriodOrder(startPeriod);
    },

    getCommentText(comment) {
      if (!comment) return "";
      return comment.commentText || comment.CommentText || "";
    },

    getCommentEnteredBy(comment) {
      if (!comment) return "";
      const enteredBy = comment.enteredBy || comment.EnteredBy || "";
      const familyId = this.selectedStudent ? this.selectedStudent.familyID : "";
      const normalizeIdentity = (value) =>
        value
          ? value
              .toString()
              .trim()
              .replace(/\s+/g, "")
              .toUpperCase()
          : "";
      if (
        enteredBy &&
        familyId &&
        normalizeIdentity(enteredBy) === normalizeIdentity(familyId)
      ) {
        return "Parent";
      }
      return enteredBy;
    },

    getCommentEnteredOn(comment) {
      if (!comment) return null;
      return comment.enteredOn || comment.EnteredOn || null;
    },

    getCommentCallerID(comment) {
      if (!comment) return "";
      return comment.callerID || comment.CallerID || "";
    },

    getCommentRecordingUrl(comment) {
      if (!comment) return null;
      return normalizeRecordingUrl(
        comment.recordingURL ||
          comment.recordingUrl ||
          comment.RecordingURL ||
          null
      );
    },

    setTodayOnlyAfternoon() {
      const today = moment().format("YYYY-MM-DD");
      this.newBusChange.startDate = today;
      this.newBusChange.endDate = today;
      this.newBusChange.startPeriod = "PM";
      this.newBusChange.endPeriod = "PM";
    },

    searchAccountByPhone() {
      this.openAccountSearch(this.newBusChange.busChangePhoneNumber);
    },

    async toggleExtraInfoEdit() {
      if (!this.editingExtraInfo) {
        this.extraInfoBackup = {
          geoPhoneNumber: this.newBusChange.geoPhoneNumber,
          note: this.newBusChange.note,
        };
        this.editingExtraInfo = true;
        return;
      }

      // Done: persist immediately when editing an existing bus change.
      if (this.isEditing && this.editingId) {
        await this.saveExtraInfo();
        return;
      }

      this.editingExtraInfo = false;
      this.extraInfoBackup = null;
    },

    async saveExtraInfo() {
      const phone = this.newBusChange.geoPhoneNumber || "";
      if (phone) {
        const digits = phone.replace(/[-\s()]/g, "");
        if (!([10, 11].includes(digits.length) && /^\d+$/.test(digits))) {
          this.$notify({
            text:
              "Geo Tracking Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      this.savingExtraInfo = true;
      try {
        const response = await this.$http.post(
          "api/BusChange/UpdateExtraInfo",
          {
            id: this.editingId,
            geoPhoneNumber: this.newBusChange.geoPhoneNumber || "",
            note: this.newBusChange.note || "",
          }
        );

        if (response && response.geoSyncError) {
          this.$notify({
            text:
              "Saved, but GeoTrack sync failed: " + response.geoSyncError,
            group: "notification",
            type: "warning",
          });
        } else {
          this.$notify({
            text: "Geo phone and note saved",
            group: "notification",
            type: "success",
          });
        }

        this.editingExtraInfo = false;
        this.extraInfoBackup = null;
      } catch (e) {
        this.$notify({
          text: "Failed to save geo phone / note",
          group: "notification",
          type: "error",
        });
      } finally {
        this.savingExtraInfo = false;
      }
    },

    cancelExtraInfoEdit() {
      if (this.extraInfoBackup) {
        this.newBusChange.geoPhoneNumber = this.extraInfoBackup.geoPhoneNumber;
        this.newBusChange.note = this.extraInfoBackup.note;
      }
      this.editingExtraInfo = false;
      this.extraInfoBackup = null;
    },

    getPlayableRecordingUrl(url) {
      return normalizeRecordingUrl(url);
    },

    async loadBusChangeComments(busChangeRequestId) {
      this.busChangeComments = [];

      if (!busChangeRequestId) {
        return;
      }

      this.loadingBusChangeComments = true;
      try {
        const response = await this.$http.get(
          `api/BusChange/GetBusChangeComments?busChangeRequestId=${busChangeRequestId}`
        );
        this.busChangeComments = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeComments = [];
      } finally {
        this.loadingBusChangeComments = false;
      }
    },

    async loadLinkedRequestForBusChange(busChangeID) {
      if (!busChangeID) return null;
      try {
        const request = await this.$http.get(
          `api/BusChangeRequests/GetLinkedRequest?busChangeID=${busChangeID}`
        );
        return request || null;
      } catch (e) {
        return null;
      }
    },

    onStartDateChange() {
      this.startDateMenu = false;
      this.startDateEdited = true;
    },

    closeDialog() {
      this.addDialog = false;
      this.resetForm();
    },

    resetForm() {
      // Account search
      this.phoneSearch = "";
      this.firstNameSearch = "";
      this.lastNameSearch = "";
      this.houseNumberSearch = "";
      this.streetSearch = "";
      this.accounts = [];
      this.selectedAccount = null;
      this.showNoAccountsMessage = false;

      // Student selection
      this.selectedStudent = null;
      this.students = [];
      this.loadingStudents = false;
      this.studentSource = this.getDefaultStudentSource();
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = false;

      // Other fields
      this.isEditing = false;
      this.editingId = null;
      this.editingRecordInfo = null;
      this.editingExtraInfo = false;
      this.savingExtraInfo = false;
      this.extraInfoBackup = null;
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.gradeError = null;
      this.startDateEdited = false;
      this.selectedGradeDeadlines = [];
      this.selectedPickupRoute = null;
      this.selectedDropoffRoute = null;
      this.selectedPickupStopNumber = null;
      this.selectedDropoffStopNumber = null;
      this.pickupRouteStops = [];
      this.dropoffRouteStops = [];
      this.loadingPickupStops = false;
      this.loadingDropoffStops = false;
      this.skipRouteWarning = false;
      this.skipGeoStopWarning = false;
      this.missingGeoStopDialog = false;
      this.missingGeoStopMessage = "";
      this.originalPickupRouteID = null;
      this.originalDropoffRouteID = null;
      this.originalNotificationPhone = null;
      this.originalNotificationType = null;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      this.newBusChange = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        notificationPhoneNumber: "",
        notificationType: "",
        note: "",
      };
    },

    async loadBusRoutes() {
      this.loadingBusRoutes = true;
      try {
        // In day camp mode, ask the backend for only the summer season's routes.
        let url = "api/buschange/GetBusRoutes";
        if (this.isDayCamp) {
          url += `?season=${encodeURIComponent(this.summerSeasonName)}`;
        }
        this.busRoutes = await this.$http.get(url);
        this.pickupRoutes = this.busRoutes.filter(
          (r) => r.direction === "Pickup"
        );
        this.dropoffRoutes = this.busRoutes.filter(
          (r) => r.direction === "Dropoff"
        );
      } catch (e) {
        this.busRoutes = [];
        this.pickupRoutes = [];
        this.dropoffRoutes = [];
      } finally {
        this.loadingBusRoutes = false;
      }
    },

    async onPickupRouteChange(pickupRoute) {
      this.selectedPickupStopNumber = null;
      this.pickupRouteStops = [];

      if (!pickupRoute) {
        return;
      }

      await this.loadRouteStops("pickup", pickupRoute.id);

      // Helper to check if two comma-separated strings have any overlap
      const hasOverlap = (str1, str2) => {
        if (!str1 || !str2) return false;
        const arr1 = str1.split(",").map((s) => s.trim());
        const arr2 = str2.split(",").map((s) => s.trim());
        return arr1.some((item) => arr2.includes(item));
      };

      const pickupBusNumber = this.getRouteBusNumber(pickupRoute);

      // Try to find exact match first (highest priority)
      const exactMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          this.getRouteBusNumber(dropoff) === pickupBusNumber &&
          dropoff.driver === pickupRoute.driver &&
          dropoff.buildings === pickupRoute.buildings &&
          dropoff.grades === pickupRoute.grades &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (exactMatch) {
        this.selectedDropoffRoute = exactMatch;
        await this.onDropoffRouteChange(exactMatch);
        return;
      }

      // Try match with overlapping buildings and grades
      const overlapMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          this.getRouteBusNumber(dropoff) === pickupBusNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.buildings, pickupRoute.buildings) &&
          hasOverlap(dropoff.grades, pickupRoute.grades) &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (overlapMatch) {
        this.selectedDropoffRoute = overlapMatch;
        await this.onDropoffRouteChange(overlapMatch);
        return;
      }

      // Try looser match - just bus number, driver, and overlapping grades
      const looseMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          this.getRouteBusNumber(dropoff) === pickupBusNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.grades, pickupRoute.grades)
        );
      });

      if (looseMatch) {
        this.selectedDropoffRoute = looseMatch;
        await this.onDropoffRouteChange(looseMatch);
      }
    },

    async onDropoffRouteChange(dropoffRoute) {
      this.selectedDropoffStopNumber = null;
      this.dropoffRouteStops = [];

      if (!dropoffRoute) {
        return;
      }

      await this.loadRouteStops("dropoff", dropoffRoute.id);
      // If pickup stop was already chosen on the same bus, mirror it.
      this.copyPickupStopToDropoffIfSameRoute(this.selectedPickupStopNumber);
    },

    onPickupStopNumberChange(stopNumber) {
      this.copyPickupStopToDropoffIfSameRoute(stopNumber);
    },

    isSameBusRoute(pickupRoute, dropoffRoute) {
      if (!pickupRoute || !dropoffRoute) return false;
      const pickupBus = this.getRouteBusNumber(pickupRoute);
      const dropoffBus = this.getRouteBusNumber(dropoffRoute);
      return !!pickupBus && pickupBus === dropoffBus;
    },

    copyPickupStopToDropoffIfSameRoute(stopNumber) {
      if (stopNumber == null || stopNumber === "") return;
      if (
        !this.isSameBusRoute(
          this.selectedPickupRoute,
          this.selectedDropoffRoute
        )
      ) {
        return;
      }
      if (
        !this.dropoffRouteStops ||
        !this.dropoffRouteStops.some((s) => s.stopNumber === stopNumber)
      ) {
        return;
      }
      this.selectedDropoffStopNumber = stopNumber;
    },

    async loadRouteStops(direction, routeId, preferredStopNumber = null) {
      if (!routeId) {
        return;
      }

      const loadingKey =
        direction === "pickup" ? "loadingPickupStops" : "loadingDropoffStops";
      const itemsKey =
        direction === "pickup" ? "pickupRouteStops" : "dropoffRouteStops";
      const selectedKey =
        direction === "pickup"
          ? "selectedPickupStopNumber"
          : "selectedDropoffStopNumber";

      this[loadingKey] = true;
      try {
        const stops = await this.$http.get(
          `api/buschange/GetRouteStops?routeId=${routeId}`
        );
        this[itemsKey] = Array.isArray(stops) ? stops : [];
        if (
          preferredStopNumber != null &&
          this[itemsKey].some((s) => s.stopNumber === preferredStopNumber)
        ) {
          this[selectedKey] = preferredStopNumber;
        }
      } catch (e) {
        this[itemsKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },

    getMissingGeoStopMessage() {
      const missing = [];
      if (this.selectedPickupRoute && !this.selectedPickupStopNumber) {
        missing.push("pickup");
      }
      if (this.selectedDropoffRoute && !this.selectedDropoffStopNumber) {
        missing.push("dropoff");
      }
      if (!missing.length) return "";
      if (missing.length === 2) {
        return "Pickup and dropoff routes are assigned, but no Geo stop was selected for either one.";
      }
      return `A ${missing[0]} route is assigned, but no Geo stop was selected.`;
    },

    async loadBranches() {
      try {
        const branchesResponse = await this.$http.get(
          "api/students/getBranchList"
        );
        let branches = Array.isArray(branchesResponse) ? branchesResponse : [];

        // Non-admin users should only see their permitted branches.
        if (!this.isAdmin() && this.permittedBranches.length) {
          const filteredBranches = branches.filter((b) =>
            this.permittedBranches.includes(b)
          );
          branches =
            filteredBranches.length > 0
              ? filteredBranches
              : this.permittedBranches;
        }

        this.branchList = branches;
      } catch (e) {
        this.branchList = [];
      }
    },

    isDayCampGrade(grade) {
      return (
        !!grade &&
        (grade.source || "").trim().toLowerCase() === "day camp"
      );
    },

    // Finds a grade rule for the given grade + scope. For day camp the scope is the
    // building; for school it's the branch. Only rules of the matching source are considered.
    getGradeSetup(grade, scope, isDayCamp = false) {
      if (!grade || !scope || !this.busChangeGrades.length) return null;
      const g = grade.trim();
      const s = scope.trim();
      return this.busChangeGrades.find((x) => {
        if (this.isDayCampGrade(x) !== isDayCamp) return false;
        if (!x.grade || x.grade.trim() !== g) return false;
        const key = isDayCamp ? x.building : x.branch;
        return key && key.trim() === s;
      });
    },

    checkGradeAllowsBusChange(grade, scope, isDayCamp = false) {
      const setup = this.getGradeSetup(grade, scope, isDayCamp);
      if (!setup)
        return {
          allowed: false,
          error: "Bus changes are not allowed for this grade.",
        };

      // Check if bus change is allowed for this grade
      if (!setup.allowBusChange) {
        return {
          allowed: false,
          error: "Bus changes are not allowed for this grade.",
        };
      }

      return { allowed: true, error: null };
    },

    async loadSelectedGradeDeadlines(grade, scope, isDayCamp = false) {
      const setup = this.getGradeSetup(grade, scope, isDayCamp);
      if (!setup || !setup.id) {
        this.selectedGradeDeadlines = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + setup.id
        );
        this.selectedGradeDeadlines = Array.isArray(response) ? response : [];
      } catch (e) {
        this.selectedGradeDeadlines = [];
      }
    },

    checkDeadlineWarning(grade, branch, startDate) {
      if (!startDate || !this.selectedGradeDeadlines.length) return null;

      // Check if start date is today or in the past
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      // startDate is in YYYY-MM-DD format - check if it's today or before
      if (startDate > todayStr) return null;

      // Get day of week for the start date
      const startDateObj = new Date(startDate + "T00:00:00");
      const dayOfWeek = startDateObj.getDay();

      // Check for exception deadline first
      const exception = this.selectedGradeDeadlines.find(
        (d) => d.exceptionDate && d.exceptionDate.startsWith(startDate)
      );

      // Get applicable deadline (exception or default for day of week)
      const deadline =
        exception ||
        this.selectedGradeDeadlines.find(
          (d) => !d.exceptionDate && d.dayOfWeek === dayOfWeek
        );

      if (!deadline || !deadline.deadline) return null;

      // Check if current time is past deadline
      const currentTime =
        today.getHours() * 3600 + today.getMinutes() * 60 + today.getSeconds();

      const deadlineParts = deadline.deadline.split(":");
      if (deadlineParts.length >= 2) {
        const deadlineSeconds =
          parseInt(deadlineParts[0]) * 3600 +
          parseInt(deadlineParts[1]) * 60 +
          (deadlineParts[2] ? parseFloat(deadlineParts[2]) : 0);

        if (currentTime > deadlineSeconds) {
          // Format to 12-hour time for display
          let hours = parseInt(deadlineParts[0]);
          const minutes = deadlineParts[1];
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          const deadlineFormatted = `${hours}:${minutes} ${ampm}`;
          return `Note: The deadline for bus changes has passed (${deadlineFormatted}).`;
        }
      }

      return null;
    },

    async searchByPhone(value) {
      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.clearAccountSearch();
        return;
      }

      this.showNoAccountsMessage = false;

      // Debounce the search
      this.searchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          if (accounts.length > 1) {
            this.accounts = accounts;
          } else if (accounts.length === 1) {
            this.selectedAccount = accounts[0];
            await this.loadStudentsByAccount();
          } else {
            this.showNoAccountsMessage = true;
            this.accounts = [];
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
        }
      }, 400);
    },

    async searchByName() {
      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const lastName = this.lastNameSearch ? this.lastNameSearch.trim() : "";
      const firstName = this.firstNameSearch ? this.firstNameSearch.trim() : "";

      if (!lastName && !firstName) {
        this.clearAccountSearch();
        return;
      }

      this.showNoAccountsMessage = false;

      // Debounce the search
      this.searchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .searchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          if (!accounts.length) {
            this.showNoAccountsMessage = true;
            this.accounts = [];
            return;
          }

          if (accounts.length === 1) {
            this.selectedAccount = accounts[0];
            await this.loadStudentsByAccount();
          } else {
            this.accounts = accounts;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
        }
      }, 400);
    },

    async searchByAddress() {
      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const houseNumber = this.houseNumberSearch
        ? this.houseNumberSearch.trim()
        : "";
      const street = this.streetSearch ? this.streetSearch.trim() : "";

      if (!houseNumber && !street) {
        this.clearAccountSearch();
        return;
      }

      this.showNoAccountsMessage = false;

      // Debounce the search
      this.searchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          if (!accounts.length) {
            this.showNoAccountsMessage = true;
            this.accounts = [];
            return;
          }

          if (accounts.length === 1) {
            this.selectedAccount = accounts[0];
            await this.loadStudentsByAccount();
          } else {
            this.accounts = accounts;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
        }
      }, 400);
    },

    async selectAccount(account) {
      this.selectedAccount = account;
      this.accounts = [];
      await this.loadStudentsByAccount();
    },

    clearAccount() {
      this.selectedAccount = null;
      this.students = [];
      this.selectedStudent = null;
      this.phoneSearch = "";
      this.firstNameSearch = "";
      this.lastNameSearch = "";
      this.houseNumberSearch = "";
      this.streetSearch = "";
      this.showNoAccountsMessage = false;
    },

    clearStudent() {
      this.selectedStudent = null;
      // Keep account and students list - user goes back to student selection
    },

    clearAccountSearch() {
      this.accounts = [];
      this.selectedAccount = null;
      this.students = [];
      this.selectedStudent = null;
      this.showNoAccountsMessage = false;
      this.houseNumberSearch = "";
      this.streetSearch = "";
    },

    async loadStudentsByAccount() {
      if (!this.selectedAccount || !this.selectedAccount.accountID) return;

      this.loadingStudents = true;
      try {
        const accountID = this.selectedAccount.accountID;
        const url = this.isDayCamp
          ? `api/daycamp/getEnrolledStudentsByAccountID?accountID=${accountID}`
          : `api/students/getStudentsByAccountID?accountID=${accountID}`;

        let students = await this.$http.get(url);

        students = students || [];

        // Filter by branch if userBranch exists (school source only; the day
        // camp endpoint already scopes to enrolled campers). The empty result
        // is surfaced by the inline info alert, so no toast is shown here.
        if (this.userBranch && !this.isDayCamp) {
          // Handle comma-separated branches like "2411,2412"
          const allowedBranches = this.userBranch
            .split(",")
            .map((b) => b.trim());
          students = students.filter((s) => allowedBranches.includes(s.branch));
        }

        this.students = students;

        // Auto-select if only one student. The empty case is handled by the
        // inline info alert, so no toast is shown here.
        if (this.students.length === 1) {
          await this.selectStudent(this.students[0]);
        }
      } catch (e) {
        this.$notify({
          text: "Failed to load students",
          group: "notification",
          type: "error",
        });
        this.students = [];
      } finally {
        this.loadingStudents = false;
      }
    },

    onStudentSourceChange() {
      // Switching source clears any selection and reloads from the new source.
      this.selectedStudent = null;
      this.students = [];
      this.gradeError = null;
      // Reload routes so the season filter (day camp only) is applied server-side.
      this.loadBusRoutes();
      if (this.selectedAccount && this.selectedAccount.accountID) {
        this.loadStudentsByAccount();
      }
    },

    filterRoutesBySeason(routes) {
      // In day camp mode, only show routes for the current summer season.
      if (!this.isDayCamp) return routes;
      // If the routes payload doesn't carry a season, leave them as-is so we
      // never hide everything (backend must include `season` to enable this).
      if (!routes.some((r) => r.season)) return routes;
      const target = this.summerSeasonName.trim().toLowerCase();
      return routes.filter(
        (r) => (r.season || "").trim().toLowerCase() === target
      );
    },

    async loadDayCampInfoForEdit(row) {
      await this.loadSummerDates();
      if (!this.summerDates || !this.selectedStudent) return;

      // Only relevant when the bus change overlaps the day camp season.
      const seasonStart = moment(this.summerDates.startDate).startOf("day");
      const seasonEnd = moment(this.summerDates.endDate).startOf("day");
      const bcStart = row.startDate
        ? moment(row.startDate).startOf("day")
        : null;
      if (!bcStart || !bcStart.isValid()) return;
      const bcEnd =
        row.endDate && moment(row.endDate).isValid()
          ? moment(row.endDate).startOf("day")
          : bcStart.clone();
      const overlapsSeason =
        bcStart.isSameOrBefore(seasonEnd) && bcEnd.isSameOrAfter(seasonStart);
      if (!overlapsSeason) return;

      const accountID = (this.selectedStudent.familyID || "")
        .toString()
        .trim();
      const studentID = (this.selectedStudent.studentID || "")
        .toString()
        .trim();
      if (!accountID || !studentID) return;

      try {
        const students = await this.$http.get(
          `api/daycamp/getEnrolledStudentsByAccountID?accountID=${accountID}`
        );
        if (!students || !students.length) return;

        const match = students.find(
          (s) =>
            s.studentID &&
            s.studentID.toString().trim().toUpperCase() ===
              studentID.toUpperCase()
        );
        if (!match) return;

        // Merge day camp info without disturbing the edited record's identity
        // (keep the existing term/regID, name, etc.). Reassign the object so
        // the added fields stay reactive.
        this.selectedStudent = Object.assign({}, this.selectedStudent, {
          isDayCamp: true,
          regType: match.regType || "",
          weeks: match.weeks || [],
          enrollmentStartDate: match.enrollmentStartDate || null,
          enrollmentEndDate: match.enrollmentEndDate || null,
          room: match.room || "",
          // Prefer the day camp building so route filtering matches the
          // student's day camp building.
          building: match.building || this.selectedStudent.building,
          class: match.class || this.selectedStudent.class,
          section: match.section || this.selectedStudent.section,
          // Use the day camp teacher (usually empty) so Room shows just like
          // the New Bus Change flow, instead of the school teacher.
          teacherH: match.teacherH || "",
          teacherH2: match.teacherH2 || "",
        });
        this.studentSource = "daycamp";
        // Reload routes filtered to the summer season now that we know this is
        // a day camp bus change (route selection happens right after this).
        await this.loadBusRoutes();
      } catch (e) {
        // Leave school info in place if day camp lookup fails.
      }
    },

    async loadSummerDates() {
      if (this.summerDates) return;
      try {
        const response = await this.$http.get("api/daycamp/getSummerDates");
        if (response && response.startDate && response.endDate) {
          this.summerDates = {
            startDate: response.startDate,
            endDate: response.endDate,
          };
        }
      } catch (e) {
        this.summerDates = null;
      }
    },

    isWithinSummerSeason() {
      if (!this.summerDates) return false;
      const today = moment().startOf("day");
      const start = moment(this.summerDates.startDate).startOf("day");
      const end = moment(this.summerDates.endDate).startOf("day");
      if (!start.isValid() || !end.isValid()) return false;
      return today.isSameOrAfter(start) && today.isSameOrBefore(end);
    },

    getDefaultStudentSource() {
      return this.isWithinSummerSeason() ? "daycamp" : "school";
    },

    getStudentHebName(student) {
      if (!student) return "";
      const first = (student.hebName || "").toString().trim();
      const last = (student.lastNameH || student.LastNameH || "")
        .toString()
        .trim();
      if (first && last) return first + " " + last;
      return first || last;
    },

    async selectStudent(student) {
      // Map student data to expected format
      const mappedStudent = {
        studentID: student.studentID,
        studentName: student.engName || student.name,
        hebName: this.getStudentHebName(student),
        building: student.building,
        branch: student.branch,
        class: student.class || student.grade, // Use class field, fallback to grade
        section: student.section || "",
        address1: student.address || "",
        regID: student.regID,
        teacherH: student.teacherH || student.teacher,
        teacherH2: student.teacherH2 || student.teacher2,
        room: student.room || "",
        // Day camp enrollment info (present only when source is day camp)
        isDayCamp: this.isDayCamp,
        regType: student.regType || "",
        weeks: student.weeks || [],
        enrollmentStartDate: student.enrollmentStartDate || null,
        enrollmentEndDate: student.enrollmentEndDate || null,
      };

      // Bus change grade rules apply to both sources: school rules are keyed by
      // Grade + Branch, day camp rules by Grade + Building.
      const gradeScope = this.isDayCamp
        ? mappedStudent.building
        : mappedStudent.branch;

      const gradeCheck = this.checkGradeAllowsBusChange(
        mappedStudent.class,
        gradeScope,
        this.isDayCamp
      );
      if (!gradeCheck.allowed) {
        this.gradeError = gradeCheck.error;
        this.$notify({
          text: gradeCheck.error,
          group: "notification",
          type: "error",
        });
        return;
      }

      this.gradeError = null;
      this.selectedStudent = mappedStudent;

      // Load deadlines for this student's grade for deadline warnings.
      await this.loadSelectedGradeDeadlines(
        mappedStudent.class,
        gradeScope,
        this.isDayCamp
      );

      // Pre-fill home address if available
      if (mappedStudent.address1) {
        this.newBusChange.homeAddress = mappedStudent.address1;
      }

      // Pre-fill geo phone number with account home phone
      if (this.selectedAccount && this.selectedAccount.homePhone) {
        this.newBusChange.geoPhoneNumber = this.selectedAccount.homePhone;
      }
    },

    getTeacherDisplay(student) {
      const teachers = [];
      if (student.teacherH) teachers.push(student.teacherH);
      if (student.teacherH2) teachers.push(student.teacherH2);
      return teachers.join(" / ");
    },

    getStudentClassExtra(student) {
      if (!student) return "";
      const teacher = this.getTeacherDisplay(student);
      if (teacher) return `Teacher: ${teacher}`;
      if (student.room) return `Room: ${student.room}`;
      return "";
    },

    getEnrollmentSummary(student) {
      if (!student) return "";
      const type = (student.regType || "").trim();
      if (!type) return "";
      const weekCount = (student.weeks || []).length;
      if (type.toUpperCase() === "CUSTOM") {
        return `Custom (${weekCount} week${weekCount === 1 ? "" : "s"})`;
      }
      return type;
    },

    getEnrollmentDisplay(student) {
      if (!student) return "";
      const type = (student.regType || "").trim();
      if (!type) return "";
      const detail = this.getEnrollmentWeeksLabel(student);
      if (type.toUpperCase() === "CUSTOM") {
        const weekCount = (student.weeks || []).length;
        const label = `Custom (${weekCount} week${
          weekCount === 1 ? "" : "s"
        })`;
        return detail
          ? `Enrolled for ${label}: ${detail}`
          : `Enrolled for ${label}`;
      }
      return detail
        ? `Enrolled for ${type} (${detail})`
        : `Enrolled for ${type}`;
    },

    getEnrollmentWeeksLabel(student) {
      if (!student) return "";
      const weeks = student.weeks || [];
      const type = (student.regType || "").trim().toUpperCase();

      // For non-custom enrollments (FULL / HALF / etc.) show the overall
      // enrolled range instead of listing every week.
      if (type !== "CUSTOM") {
        const start =
          student.enrollmentStartDate ||
          (weeks.length ? weeks[0].startDate : null);
        const end =
          student.enrollmentEndDate ||
          (weeks.length ? weeks[weeks.length - 1].endDate : null);
        if (start && end) {
          return `${moment(start).format("MMM D")}–${moment(end).format(
            "MMM D"
          )}`;
        }
        return "";
      }

      if (!weeks.length) {
        if (student.enrollmentStartDate && student.enrollmentEndDate) {
          return `${moment(student.enrollmentStartDate).format(
            "MMM D"
          )}–${moment(student.enrollmentEndDate).format("MMM D")}`;
        }
        return "";
      }
      return weeks
        .map((w) => {
          const label = w.parshe ? w.parshe : `Week ${w.weekNumber}`;
          const dates =
            w.startDate && w.endDate
              ? ` (${moment(w.startDate).format("MMM D")}–${moment(
                  w.endDate
                ).format("MMM D")})`
              : "";
          return label + dates;
        })
        .join(", ");
    },

    getEnrollmentDateWarning() {
      if (!this.isDayCamp || !this.selectedStudent) return null;

      const weeks = this.selectedStudent.weeks || [];
      if (!weeks.length) return null;

      const start = this.newBusChange.startDate;
      const end = this.newBusChange.endDate;
      if (!start || !end) return null;

      const reqStart = moment(start, "YYYY-MM-DD").startOf("day");
      const reqEnd = moment(end, "YYYY-MM-DD").startOf("day");
      if (!reqStart.isValid() || !reqEnd.isValid()) return null;
      if (reqEnd.isBefore(reqStart)) return null;

      const ranges = weeks
        .filter((w) => w.startDate && w.endDate)
        .map((w) => ({
          s: moment(w.startDate).startOf("day"),
          e: moment(w.endDate).startOf("day"),
        }))
        .filter((r) => r.s.isValid() && r.e.isValid());
      if (!ranges.length) return null;

      // Every day in the requested range must fall within an enrolled week.
      // A small tolerance bridges weekend gaps between adjacent enrolled weeks
      // while still flagging real multi-week gaps (e.g. custom weeks 1-3, 7-9).
      const covers = (day) =>
        ranges.some(
          (r) =>
            day.isSameOrAfter(r.s) &&
            day.isSameOrBefore(r.e.clone().add(2, "days"))
        );

      const cursor = reqStart.clone();
      let hasUncovered = false;
      while (cursor.isSameOrBefore(reqEnd)) {
        if (!covers(cursor)) {
          hasUncovered = true;
          break;
        }
        cursor.add(1, "day");
      }
      if (!hasUncovered) return null;

      return (
        "This student is only enrolled in day camp for: " +
        this.getEnrollmentWeeksLabel(this.selectedStudent) +
        ". The selected date range includes dates outside their enrollment."
      );
    },

    openAccountSearch(prefillPhone) {
      const phone =
        typeof prefillPhone === "string" ? prefillPhone : "";
      // Reset dialog search state
      this.dialogPhoneSearch = phone || "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
      this.accountSearchDialog = true;
      if (this.dialogPhoneSearch) {
        this.searchDialogByPhone(this.dialogPhoneSearch);
      }
    },

    closeAccountSearchDialog() {
      this.accountSearchDialog = false;
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    async searchDialogByPhone(value) {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByName() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const lastName = this.dialogLastNameSearch
        ? this.dialogLastNameSearch.trim()
        : "";
      const firstName = this.dialogFirstNameSearch
        ? this.dialogFirstNameSearch.trim()
        : "";

      if (!lastName && !firstName) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .dialogSearchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByAddress() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const houseNumber = this.dialogHouseNumberSearch
        ? this.dialogHouseNumberSearch.trim()
        : "";
      const street = this.dialogStreetSearch
        ? this.dialogStreetSearch.trim()
        : "";

      if (!houseNumber && !street) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.newBusChange.busChangeAccountID = account.accountID;
      // User explicitly picked this account — replace whatever was entered
      this.newBusChange.busChangeName =
        account.name ||
        ((account.lastName || "") + " " + (account.firstName || "")).trim();
      if (account.address1) {
        this.newBusChange.busChangeAddress = account.address1;
      }
      if (!this.newBusChange.busChangePhoneNumber) {
        const accountPhone = account.homePhone || account.cellPhone || "";
        this.newBusChange.busChangePhoneNumber = accountPhone
          ? this.formatPhoneNumber(accountPhone) || accountPhone
          : "";
      }

      // Clear dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    validateForm() {
      // Check required fields
      if (!this.newBusChange.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.endDate) {
        this.$notify({
          text: "End Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone numbers if they have values
      const validatePhone = (phoneNumber) => {
        if (!phoneNumber) return true; // Empty is ok (not required)
        const digits = phoneNumber.replace(/[-\s()]/g, "");
        return [10, 11].includes(digits.length) && /^\d+$/.test(digits);
      };

      if (
        this.newBusChange.busChangePhoneNumber &&
        !validatePhone(this.newBusChange.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Bus Change Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.newBusChange.geoPhoneNumber &&
        !validatePhone(this.newBusChange.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.newBusChange.notificationPhoneNumber &&
        !validatePhone(this.newBusChange.notificationPhoneNumber)
      ) {
        this.$notify({
          text:
            "Notification Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      return true;
    },

    async saveBusChange() {
      if (!this.validateForm()) {
        // Validation error already notified in validateForm()
        return;
      }

      // Check grade settings before saving (school source only).
      if (!this.isDayCamp) {
        const gradeCheck = this.checkGradeAllowsBusChange(
          this.selectedStudent.class,
          this.selectedStudent.branch
        );
        if (!gradeCheck.allowed) {
          this.$notify({
            text: gradeCheck.error,
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      // Validate endDate is not before startDate
      if (this.newBusChange.endDate && this.newBusChange.startDate) {
        if (this.newBusChange.endDate < this.newBusChange.startDate) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return;
        }

        if (
          this.newBusChange.endDate === this.newBusChange.startDate &&
          this.isEndPeriodBeforeStartPeriod(
            this.newBusChange.startPeriod,
            this.newBusChange.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      // Day camp: warn (soft) if the date range falls outside enrolled weeks.
      if (!this.skipEnrollmentWarning && this.getEnrollmentDateWarning()) {
        this.enrollmentWarningDialog = true;
        return;
      }

      // Check if notification fields were cleared for IVR/ONLINE sources
      if (this.isEditing && this.isSourceMandatory && !this.skipNotification) {
        const hadNotificationInfo =
          this.originalNotificationPhone || this.originalNotificationType;
        const currentHasNotificationInfo =
          this.newBusChange.notificationPhoneNumber &&
          this.newBusChange.notificationType;

        if (hadNotificationInfo && !currentHasNotificationInfo) {
          this.clearedNotificationWarningDialog = true;
          return;
        }
      }

      // Check for incomplete route selection warning rules
      if (!this.skipRouteWarning) {
        const hasPickup =
          this.selectedPickupRoute && this.selectedPickupRoute.id;
        const hasDropoff =
          this.selectedDropoffRoute && this.selectedDropoffRoute.id;
        const originalHasPickup = !!this.originalPickupRouteID;
        const originalHasDropoff = !!this.originalDropoffRouteID;
        const originallyNoRoutes = !originalHasPickup && !originalHasDropoff;
        const hasOnlyOneRoute =
          (hasPickup && !hasDropoff) || (!hasPickup && hasDropoff);

        if ((!hasPickup && !hasDropoff) || (hasOnlyOneRoute && originallyNoRoutes)) {
          this.incompleteRouteDialog = true;
          return;
        }
      }

      // Warn when a route is assigned without a Geo stop (GeoTrack will not sync)
      if (!this.skipGeoStopWarning) {
        const missingGeoStopMsg = this.getMissingGeoStopMessage();
        if (missingGeoStopMsg) {
          this.missingGeoStopMessage = missingGeoStopMsg;
          this.missingGeoStopDialog = true;
          return;
        }
      }

      // Check if notification should be sent (routes changed and at least one route assigned)
      if (!this.skipNotification) {
        const currentPickupID = this.selectedPickupRoute
          ? this.selectedPickupRoute.id
          : null;
        const currentDropoffID = this.selectedDropoffRoute
          ? this.selectedDropoffRoute.id
          : null;

        // Check if routes were changed or assigned for the first time
        const routesChanged =
          currentPickupID !== this.originalPickupRouteID ||
          currentDropoffID !== this.originalDropoffRouteID;

        // Allow notification when at least one route is assigned
        const atLeastOneRouteSelected = currentPickupID || currentDropoffID;
        const hasPendingLinkedRequest = this.hasPendingLinkedRequest();

        if ((routesChanged || hasPendingLinkedRequest) && atLeastOneRouteSelected) {
          // Check if notification fields are filled
          const hasNotificationPhone = this.newBusChange
            .notificationPhoneNumber;
          const hasNotificationType = this.newBusChange.notificationType;

          if (!hasNotificationPhone || !hasNotificationType) {
            // Show missing fields dialog
            this.missingNotificationFieldsDialog = true;
            return;
          } else {
            // Show notification confirmation dialog
            this.notificationConfirmDialog = true;
            return;
          }
        }
      }

      this.saving = true;
      try {
        const data = {
          studentID: this.selectedStudent.studentID,
          startDate: this.newBusChange.startDate
            ? moment(this.newBusChange.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.newBusChange.endDate
            ? moment(this.newBusChange.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.newBusChange.startPeriod,
          endPeriod: this.newBusChange.endPeriod,
          homeAddress: this.selectedStudent.address1 || "",
          busChangeAddress: this.newBusChange.busChangeAddress,
          busChangePhoneNumber: this.newBusChange.busChangePhoneNumber,
          geoPhoneNumber: this.newBusChange.geoPhoneNumber,
          busChangeName: this.newBusChange.busChangeName,
          busChangeAccountID: this.newBusChange.busChangeAccountID,
          notificationPhoneNumber: this.newBusChange.notificationPhoneNumber,
          notificationType: this.newBusChange.notificationType,
          pickupRouteID: this.selectedPickupRoute
            ? this.selectedPickupRoute.id
            : null,
          dropoffRouteID: this.selectedDropoffRoute
            ? this.selectedDropoffRoute.id
            : null,
          pickupStopNumber: this.selectedPickupStopNumber,
          dropoffStopNumber: this.selectedDropoffStopNumber,
          note: this.newBusChange.note,
          building: this.selectedStudent.building,
          branch: this.selectedStudent.branch,
          class: this.selectedStudent.class,
          section: this.selectedStudent.section,
          term: this.selectedStudent.regID,
          enteredBy: this.$store.state.authentication.user,
          source: "ADMIN PORTAL",
        };

        // Include ID when editing
        if (this.isEditing && this.editingId) {
          data.id = this.editingId;
        }

        const response = await this.$http.post("api/buschange/save", data);

        if (response.error) {
          return this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
        }

        // Get the bus change ID from response
        const busChangeId = response.id || this.editingId;

        this.$notify({
          text: this.isEditing
            ? "Bus change updated successfully"
            : "Bus change saved successfully",
          group: "notification",
          type: "success",
        });

        if (response.geoSyncError) {
          this.$notify({
            group: "notification",
            type: "error",
            text: response.geoSyncError,
          });
        }

        // Send notification if needed
        if (this.savedBusChangeId && busChangeId) {
          await this.sendBusChangeNotification(busChangeId);
        }

        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to save bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
        this.skipRouteWarning = false;
        this.skipGeoStopWarning = false;
        this.skipNotification = false;
        this.skipEnrollmentWarning = false;
        this.savedBusChangeId = null;
      }
    },

    confirmSaveWithIncompleteRoute() {
      this.incompleteRouteDialog = false;
      this.skipRouteWarning = true;
      this.saveBusChange();
    },

    confirmSaveWithoutGeoStop() {
      this.missingGeoStopDialog = false;
      this.skipGeoStopWarning = true;
      this.saveBusChange();
    },

    confirmSaveWithEnrollmentWarning() {
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = true;
      this.saveBusChange();
    },

    cancelEnrollmentWarning() {
      this.enrollmentWarningDialog = false;
      this.skipEnrollmentWarning = false;
    },

    // Notification dialog handlers
    proceedWithNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = true; // Flag to send notification after save
      this.saveBusChange();
    },

    proceedWithoutNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveBusChange();
    },

    cancelSave() {
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      this.clearedNotificationWarningDialog = false;
      this.skipRouteWarning = false;
      this.skipNotification = false;
      this.skipEnrollmentWarning = false;
      this.savedBusChangeId = null;
    },

    saveWithoutNotification() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveBusChange();
    },

    closeMissingFieldsDialog() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      // User can now fill in the fields and try saving again
    },

    cancelClearedNotificationWarning() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      // User stays in edit mode to add notification fields back
    },

    confirmSaveWithoutNotification() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveBusChange();
    },

    async sendBusChangeNotification(busChangeId) {
      this.sendingNotification = true;
      try {
        const apiUrl = `https://broadcast-3t6ck5zrrq-ue.a.run.app/busChangeNotification/sendBusChangeNotification?busChangeId=${busChangeId}`;
        const response = await this.$http.post(apiUrl, {});
        const notices = getBusChangeNotificationNotices(response);
        notices.forEach((notice) => {
          this.$notify({
            text: notice.text,
            group: "notification",
            type: notice.type,
            duration: 8000,
          });
        });
      } catch (error) {
        this.$notify({
          text:
            "Failed to send bus change notification. The bus change was saved but notification failed.",
          group: "notification",
          type: "warning",
        });
      } finally {
        this.sendingNotification = false;
      }
    },

    // Delete methods
    openDeleteDialog() {
      this.recordToDelete = {
        id: this.editingId,
        studentName: this.selectedStudent
          ? this.selectedStudent.studentName || this.selectedStudent.hebName
          : "",
        studentID: this.selectedStudent ? this.selectedStudent.studentID : "",
        branch: this.selectedStudent ? this.selectedStudent.branch : "",
        class: this.selectedStudent ? this.selectedStudent.class : "",
        section: this.selectedStudent ? this.selectedStudent.section : "",
        startDate: this.newBusChange.startDate
          ? this.formatDate(this.newBusChange.startDate)
          : "",
        endDate: this.newBusChange.endDate
          ? this.formatDate(this.newBusChange.endDate)
          : "",
        startPeriod: this.newBusChange.startPeriod || "",
        endPeriod: this.newBusChange.endPeriod || "",
        busChangeAddress: this.newBusChange.busChangeAddress || "",
      };
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.recordToDelete || !this.recordToDelete.id) return;

      this.deleting = true;
      try {
        await this.$http.post("api/busChange/delete", [this.recordToDelete.id]);

        this.$notify({
          text: "Bus change deleted successfully",
          group: "notification",
          type: "success",
        });

        this.deleteDialog = false;
        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to delete bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deleting = false;
      }
    },

    // Grades Setup methods
    async openGradesSetupDialog() {
      this.gradesSetupDialog = true;
      this.editingGrade = false;
      this.selectedGrades = [];
      this.loadingGradesSetup = true;
      await Promise.all([
        this.loadBusChangeGrades(),
        this.loadBranches(),
        this.loadUserDayCampBuildings(),
      ]);
      this.loadingGradesSetup = false;
    },

    closeGradesSetupDialog() {
      this.gradesSetupDialog = false;
      this.editingGrade = false;
      this.selectedGrades = [];
      this.resetCurrentGrade();
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeGrades"
        );
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    async openAddGrade() {
      this.resetCurrentGrade();
      // Default the new grade's source to whichever tab is currently active.
      this.currentGrade.source = this.gradesSetupSourceFilter;

      if (this.isCurrentGradeDayCamp) {
        await this.loadBuildingList();
        if (
          this.hasUserDayCampBuildings() &&
          this.userDayCampBuildings.length === 1
        ) {
          this.currentGrade.building = this.userDayCampBuildings[0];
          await this.loadAvailableGrades();
        }
      } else if (!this.isAdmin() && this.permittedBranches.length === 1) {
        // Only auto-select when there is a single permitted branch.
        const onlyBranch = this.permittedBranches[0];
        this.currentGrade.branch = onlyBranch;
        await this.loadAvailableGrades();
      }
      this.editingGrade = true;
    },

    async onGradeSourceChange() {
      // Switching source resets the scope (branch/building) and grade selections.
      this.currentGrade.grade = "";
      this.currentGrade.branch = "";
      this.currentGrade.building = "";
      this.multipleGrades = [];
      this.availableGrades = [];

      if (this.isCurrentGradeDayCamp) {
        await this.loadBuildingList();
        if (
          this.hasUserDayCampBuildings() &&
          this.userDayCampBuildings.length === 1
        ) {
          this.currentGrade.building = this.userDayCampBuildings[0];
          await this.loadAvailableGrades();
        }
      } else if (!this.isAdmin() && this.permittedBranches.length === 1) {
        this.currentGrade.branch = this.permittedBranches[0];
        await this.loadAvailableGrades();
      }
    },

    async onGradeScopeChange() {
      // The scope (branch or building) drives the list of available grades.
      this.currentGrade.grade = "";
      this.multipleGrades = [];
      if (this.gradeScopeSelected) {
        await this.loadAvailableGrades();
      } else {
        this.availableGrades = [];
      }
    },

    async loadAvailableGrades() {
      this.loadingAvailableGrades = true;
      try {
        const url = this.isCurrentGradeDayCamp
          ? "api/students/getDayCampGradeList?building=" +
            encodeURIComponent(this.currentGrade.building || "")
          : "api/students/getGradeList?branch=" +
            encodeURIComponent(this.currentGrade.branch || "");
        const response = await this.$http.get(url);
        this.availableGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.availableGrades = [];
      } finally {
        this.loadingAvailableGrades = false;
      }
    },

    async loadBuildingList() {
      this.loadingBuildingList = true;
      try {
        const response = await this.$http.get(
          "api/students/getDayCampBuildingList"
        );
        let buildings = Array.isArray(response) ? response : [];

        if (this.hasUserDayCampBuildings()) {
          const filtered = buildings.filter((b) =>
            this.userDayCampBuildings.includes(b ? String(b).trim() : "")
          );
          buildings =
            filtered.length > 0
              ? filtered
              : this.userDayCampBuildings.slice();
        }

        this.buildingList = buildings;
      } catch (e) {
        this.buildingList = this.hasUserDayCampBuildings()
          ? this.userDayCampBuildings.slice()
          : [];
      } finally {
        this.loadingBuildingList = false;
      }
    },

    async openEditGrade(grade) {
      const isDayCamp = this.isDayCampGrade(grade);
      this.currentGrade = {
        id: grade.id,
        grade: grade.grade,
        branch: grade.branch || "",
        building: grade.building || "",
        source: isDayCamp ? "daycamp" : "school",
        allowBusChange: grade.allowBusChange,
      };
      this.editingGrade = true;

      const tasks = [this.loadAvailableGrades(), this.loadGradeDeadlines(grade.id)];
      if (isDayCamp) {
        tasks.push(this.loadBuildingList());
      }
      await Promise.all(tasks);
    },

    async loadGradeDeadlines(gradeId) {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + gradeId
        );
        const deadlines = Array.isArray(response) ? response : [];
        this.currentDeadlines = deadlines.filter((d) => !d.exceptionDate);
        this.currentExceptions = deadlines.filter((d) => d.exceptionDate);
      } catch (e) {
        this.currentDeadlines = [];
        this.currentExceptions = [];
      }
    },

    resetCurrentGrade() {
      this.currentGrade = {
        id: null,
        grade: "",
        branch: "",
        building: "",
        source: this.gradesSetupSourceFilter || "school",
        allowBusChange: true,
      };
      this.currentDeadlines = [];
      this.currentExceptions = [];
      this.availableGrades = [];
      this.multipleGrades = [];
      this.showUnsavedWarning = false;
    },

    backToGradesList() {
      this.editingGrade = false;
      this.resetCurrentGrade();
    },

    formatTimeDisplay(timeStr) {
      if (!timeStr) return "";
      const parts = timeStr.split(":");
      if (parts.length < 2) return "";
      let hours = parseInt(parts[0]);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return hours + ":" + minutes + " " + ampm;
    },

    parseTimeForPicker(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      return parts[0].padStart(2, "0") + ":" + parts[1];
    },

    getDefaultDeadline(dayOfWeek) {
      const deadline = this.currentDeadlines.find(
        (d) => d.dayOfWeek === dayOfWeek
      );
      return deadline ? deadline.deadline : null;
    },

    setDefaultDeadline(dayOfWeek, timeValue) {
      const existing = this.currentDeadlines.find(
        (d) => d.dayOfWeek === dayOfWeek
      );
      if (existing) {
        existing.deadline = timeValue + ":00";
        existing._changed = true;
      } else {
        this.currentDeadlines.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: dayOfWeek,
          type: "Default",
          exceptionDate: null,
          deadline: timeValue + ":00",
          _isNew: true,
        });
      }
    },

    getDefaultDeadlineAsDate(dayOfWeek) {
      const timeStr = this.getDefaultDeadline(dayOfWeek);
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setDefaultDeadlineFromDate(dayOfWeek, dateValue) {
      if (!dateValue) return;
      const hours = String(dateValue.getHours()).padStart(2, "0");
      const minutes = String(dateValue.getMinutes()).padStart(2, "0");
      this.setDefaultDeadline(dayOfWeek, `${hours}:${minutes}`);
    },

    getExceptionDeadlineAsDate() {
      const timeStr = this.newException.deadline;
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setExceptionDeadlineFromDate(e) {
      if (!e.value) return;
      const hours = String(e.value.getHours()).padStart(2, "0");
      const minutes = String(e.value.getMinutes()).padStart(2, "0");
      this.newException.deadline = `${hours}:${minutes}:00`;
    },

    getDaysWithDeadlines(excludeDay) {
      return this.weekDays.filter(
        (d) => d.value !== excludeDay && this.getDefaultDeadline(d.value)
      );
    },

    copyDeadlineFromDay(fromDay, toDay) {
      const sourceDeadline = this.getDefaultDeadline(fromDay);
      if (sourceDeadline) {
        const timeValue = this.parseTimeForPicker(sourceDeadline);
        this.setDefaultDeadline(toDay, timeValue);
      }
    },

    addException() {
      this.editingException = null;
      this.newException = { exceptionDate: "", deadline: "" };
      this.exceptionDialog = true;
    },

    editException(exception) {
      this.editingException = exception;
      this.newException = {
        exceptionDate: this.formatDateForPicker(exception.exceptionDate),
        deadline: exception.deadline,
      };
      this.exceptionDialog = true;
    },

    saveException() {
      if (!this.newException.exceptionDate || !this.newException.deadline) {
        this.$notify({
          text: "Please fill in date and time",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (this.editingException) {
        this.editingException.exceptionDate = this.newException.exceptionDate;
        this.editingException.deadline = this.newException.deadline;
        this.editingException._changed = true;
      } else {
        this.currentExceptions.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: new Date(this.newException.exceptionDate).getDay(),
          type: "Exception",
          exceptionDate: this.newException.exceptionDate,
          deadline: this.newException.deadline,
          _isNew: true,
        });
      }
      this.showUnsavedWarning = true;
      this.exceptionDialog = false;
    },

    removeException(exception) {
      if (exception.id) {
        exception._deleted = true;
      } else {
        const index = this.currentExceptions.indexOf(exception);
        if (index > -1) {
          this.currentExceptions.splice(index, 1);
        }
      }

      this.showUnsavedWarning = true;
      this.$forceUpdate();
    },

    formatExceptionDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatExceptionDateShort(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },

    confirmDeleteGrade(grade) {
      this.gradeToDelete = grade;
      this.deleteGradeDialog = true;
    },

    async deleteGrade() {
      this.deletingGrade = true;
      try {
        await this.$http.post(
          "api/busChange/deleteBusChangeGrade?id=" + this.gradeToDelete.id
        );
        this.$notify({
          text: "Grade deleted successfully",
          group: "notification",
          type: "success",
        });
        this.deleteGradeDialog = false;
        await this.loadBusChangeGrades();
      } catch (e) {
        this.$notify({
          text: e.message || "Failed to delete grade",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deletingGrade = false;
      }
    },

    async saveCurrentGrade() {
      // Validation: day camp rules require a Building, school rules require a Branch.
      if (this.isCurrentGradeDayCamp && !this.currentGrade.building) {
        this.$notify({
          text: "Please select a building",
          group: "notification",
          type: "error",
        });
        return;
      }
      if (!this.isCurrentGradeDayCamp && !this.currentGrade.branch) {
        this.$notify({
          text: "Please select a branch",
          group: "notification",
          type: "error",
        });
        return;
      }

      // For new grades, check multipleGrades array
      const isNewGrade = !this.currentGrade.id;
      if (isNewGrade && this.multipleGrades.length === 0) {
        this.$notify({
          text: "Please select at least one grade",
          group: "notification",
          type: "error",
        });
        return;
      }

      // For editing, check single grade
      if (!isNewGrade && !this.currentGrade.grade) {
        this.$notify({
          text: "Please select a grade",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingGradesSetup = true;

      // Get list of grades to save
      const gradesToSave = isNewGrade
        ? this.multipleGrades
        : [this.currentGrade.grade];

      const successGrades = [];
      const failedGrades = [];

      for (const gradeName of gradesToSave) {
        try {
          // Save grade
          const gradeResponse = await this.$http.post(
            "api/busChange/saveBusChangeGrade",
            {
              id: isNewGrade ? 0 : this.currentGrade.id,
              grade: gradeName,
              source: this.isCurrentGradeDayCamp ? "Day Camp" : "School",
              branch: this.isCurrentGradeDayCamp
                ? null
                : this.currentGrade.branch,
              building: this.isCurrentGradeDayCamp
                ? this.currentGrade.building
                : null,
              allowBusChange: this.currentGrade.allowBusChange,
              createdBy: this.$store.state.authentication.user,
            }
          );

          const gradeId = gradeResponse.id || this.currentGrade.id;

          // Save/update deadlines for each grade
          const allDeadlines = [
            ...this.currentDeadlines,
            ...this.currentExceptions,
          ];
          for (const deadline of allDeadlines) {
            if (deadline._deleted && deadline.id) {
              await this.$http.post(
                "api/busChange/deleteBusChangeDeadline?id=" + deadline.id
              );
            } else if (deadline._isNew || deadline._changed) {
              await this.$http.post("api/busChange/saveBusChangeDeadline", {
                id: isNewGrade ? 0 : deadline.id || 0,
                gradeID: gradeId,
                dayOfWeek: deadline.dayOfWeek,
                type: deadline.type,
                exceptionDate: deadline.exceptionDate,
                deadline: deadline.deadline,
                createdBy: this.$store.state.authentication.user,
              });
            }
          }

          successGrades.push(gradeName);
        } catch (e) {
          failedGrades.push(gradeName);
        }
      }

      // Show appropriate notification
      if (gradesToSave.length === 1) {
        // Single grade - simple message
        if (successGrades.length === 1) {
          this.$notify({
            text: "Grade saved successfully",
            group: "notification",
            type: "success",
          });
        } else {
          this.$notify({
            text: "Failed to save grade",
            group: "notification",
            type: "error",
          });
        }
      } else {
        // Multiple grades - show summary
        if (failedGrades.length === 0) {
          this.$notify({
            text: `All ${successGrades.length} grades saved successfully`,
            group: "notification",
            type: "success",
          });
        } else if (successGrades.length === 0) {
          this.$notify({
            text: `Failed to save all ${failedGrades.length} grades`,
            group: "notification",
            type: "error",
          });
        } else {
          this.$notify({
            text: `Saved: ${successGrades.join(", ")}`,
            group: "notification",
            type: "success",
          });
          this.$notify({
            text: `Failed: ${failedGrades.join(", ")}`,
            group: "notification",
            type: "error",
          });
        }
      }

      await this.loadBusChangeGrades();

      // Only go back if at least one succeeded or if single grade
      if (successGrades.length > 0 || gradesToSave.length === 1) {
        this.backToGradesList();
      }

      this.savingGradesSetup = false;
    },

    // Bulk Exception Methods
    openBulkExceptionDialog() {
      if (this.selectedGrades.length === 0) {
        this.$notify({
          text: "Please select at least one grade",
          group: "notification",
          type: "error",
        });
        return;
      }
      this.bulkException = { exceptionDate: "", deadline: "" };
      this.bulkExceptionDialog = true;
    },

    getSelectedGradeDetails() {
      return this.busChangeGrades.filter((g) =>
        this.selectedGrades.includes(g.id)
      );
    },

    getBulkExceptionDeadlineAsDate() {
      const timeStr = this.bulkException.deadline;
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setBulkExceptionDeadlineFromDate(e) {
      if (!e.value) return;
      const hours = String(e.value.getHours()).padStart(2, "0");
      const minutes = String(e.value.getMinutes()).padStart(2, "0");
      this.bulkException.deadline = `${hours}:${minutes}:00`;
    },

    async saveBulkException() {
      // Validation
      if (!this.bulkException.exceptionDate || !this.bulkException.deadline) {
        this.$notify({
          text: "Please fill in date and time",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingBulkException = true;
      try {
        // Convert deadline to TimeSpan format (HH:mm:ss)
        const deadlineTimeSpan = this.bulkException.deadline;

        const response = await this.$http.post(
          "api/busChange/BulkAddExceptions",
          {
            gradeIDs: this.selectedGrades,
            exceptionDate: this.bulkException.exceptionDate,
            deadline: deadlineTimeSpan,
            createdBy: this.$store.state.authentication.user,
          }
        );

        this.$notify({
          text: `Exception added to ${
            this.selectedGrades.length
          } grade(s) successfully`,
          group: "notification",
          type: "success",
        });

        this.bulkExceptionDialog = false;
        this.selectedGrades = [];
        await this.loadBusChangeGrades();
      } catch (e) {
        // this.$notify({
        //   text: e.message || e.Message || "Failed to add exception",
        //   group: "notification",
        //   type: "error",
        // });
      } finally {
        this.savingBulkException = false;
      }
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY");
    },
  },
};
</script>

<style scoped>
.bus-change-dialog .dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.bus-change-dialog .dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.bus-change-dialog .section-header {
  font-weight: 600;
  color: rgb(92, 184, 96);
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f5e9;
}

.dialog-body {
  background: #f2f4f2;
}

.form-section-card {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
}

.geo-stop-picker {
  background: #f7faf7;
  border: 1px dashed #c5d6c5;
  border-radius: 8px;
  padding: 8px 8px 2px;
}

.geo-stop-selection {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.geo-stop-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #2e7d32;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.geo-stop-address {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.geo-stop-item-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.student-list-container {
  max-height: 230px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.student-list {
  background: transparent;
}

.student-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.student-item:hover {
  background-color: #e8f5e9 !important;
}

.student-item:last-child {
  border-bottom: none;
}

.selected-student-card {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 2px solid rgb(92, 184, 96) !important;
  border-radius: 12px;
  padding: 8px;
}

.record-info-with-audio {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.record-info-compact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 11px;
  color: #757575;
  padding: 4px 8px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  flex: 1;
  min-width: 300px;
}

.record-info-compact .info-item {
  display: inline-flex;
  align-items: center;
}

.record-info-compact .info-divider {
  margin: 0 8px;
  color: #bdbdbd;
}

.recording-inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
  border-radius: 8px;
  border: 1px solid #ffcccc;
  box-shadow: 0 1px 3px rgba(255, 107, 107, 0.1);
  min-width: 280px;
}

.recording-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.recording-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.recording-label {
  font-size: 11px;
  font-weight: 600;
  color: #d63031;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recording-player {
  width: 100%;
  max-width: 280px;
  height: 32px;
  border-radius: 4px;
}

.comments-card {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
  max-height: 460px;
  overflow-y: auto;
}

.compact-info {
  background: #ffffff;
  border: 1px solid #e2e8e2 !important;
  border-radius: 8px !important;
}

.info-row {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 12px;
  color: #757575;
  min-width: 84px;
  padding-right: 8px;
  flex-shrink: 0;
}

.info-value {
  font-size: 12px;
  color: #212121;
  font-weight: 500;
  flex: 1;
}

.info-value-full {
  margin-left: 0;
  word-break: break-word;
}

.info-row-audio {
  align-items: center;
}

.info-value-audio {
  display: flex;
  align-items: center;
}

.inline-audio-player {
  width: 100%;
  max-width: 230px;
  height: 30px;
}

.original-request-card {
  background: #fff5f5;
  border-color: #ffd3d3 !important;
}

.pending-request-indicator {
  font-size: 12px;
  font-weight: 600;
  color: #b71c1c;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 6px;
  padding: 6px 8px;
}

.comment-entry {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: white;
  text-align: left !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.comment-entry:last-child {
  margin-bottom: 0;
}

.comment-meta {
  font-size: 12px;
  color: #616161;
  margin-bottom: 4px;
  text-align: left !important;
  width: 100%;
}

.comment-caller {
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 0;
  text-align: left !important;
  width: 100%;
}

.comment-text {
  font-size: 13px;
  white-space: normal;
  text-align: left !important;
  line-height: 1.4;
  width: 100%;
  margin-top: 8px;
  word-break: break-word;
}

.comment-audio {
  width: 100%;
  min-height: 36px;
}

.comment-awaiting {
  font-size: 11px;
  font-weight: 600;
  color: #b26a00;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 4px;
  padding: 2px 6px;
}

.period-helper-text {
  line-height: 1.35;
  padding-left: 2px;
}

.period-section-card {
  background: #fcfcfc;
  border-color: #e9e9e9 !important;
}

.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.flex-grow-1 {
  flex-grow: 1;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

/* Exception Deadlines Compact Design */
.exceptions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.exception-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 6px 8px 6px 12px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.exception-chip:hover {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-color: rgb(92, 184, 96);
}

.exception-content {
  display: flex;
  align-items: center;
}

.exception-date {
  font-weight: 500;
  color: #424242;
}

.exception-divider {
  margin: 0 6px;
  color: #bdbdbd;
}

.exception-time {
  color: #616161;
}

.exception-actions {
  display: flex;
  align-items: center;
  margin-left: 4px;
}

.exception-actions .action-btn {
  width: 22px;
  height: 22px;
  margin-left: 2px;
}

.exception-actions .action-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.no-exceptions {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
}

/* Copy from link styling */
.copy-from-link {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #4caf50;
  cursor: pointer;
  margin-top: 4px;
  transition: color 0.2s ease;
}

.copy-from-link:hover {
  color: #388e3c;
  text-decoration: underline;
}

.copy-from-link .v-icon {
  color: #4caf50 !important;
  font-size: 12px !important;
}

.copy-from-link:hover .v-icon {
  color: #388e3c !important;
}

/* Grades List Compact Design */
.grades-list {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
}

.grade-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.grade-row:last-child {
  border-bottom: none;
}

.grade-row:hover {
  background-color: #f5f5f5;
}

.grade-select {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.grade-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.grade-name {
  font-weight: 600;
  color: #424242;
  min-width: 80px;
}

.grade-branch {
  color: #757575;
  font-size: 13px;
  margin-left: 16px;
}

.grade-actions {
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.15s ease;
}

.grade-row:hover .grade-actions {
  opacity: 1;
}

.no-grades {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafafa;
}

.compact-search {
  border-radius: 6px;
}

/* Deadline Time Picker */
.deadline-time-picker {
  width: 100%;
}

/* Unsaved Warning */
.unsaved-warning {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #f57c00;
  padding: 8px 12px;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 4px;
}

/* Route Selection Styling */
.route-selection-wrapper {
  width: 100%;
  overflow: hidden;
}

.route-selection-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  line-height: 1.4;
}

.route-selection-driver {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
  margin-top: 2px;
  line-height: 1.2;
}

.route-detail-chip {
  display: inline-flex;
  align-items: center;
  background: rgb(92, 184, 96); /* Default fallback color */
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Add spacing between route list items - only for bus route selections */
.bus-change-dialog .route-list-item {
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 8px;
  transition: all 0.2s ease;
}

.bus-change-dialog .route-list-item:hover {
  background-color: rgba(92, 184, 96, 0.05);
}

/* Account Search Styles */
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.account-list-container {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.account-list {
  background: transparent;
}

.account-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.account-item:hover {
  background-color: #e8f5e9 !important;
}

.account-item:last-child {
  border-bottom: none;
}

.selected-account-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #2196f3 !important;
  border-radius: 12px;
  padding: 8px;
}

/* Selected Grades List in Bulk Exception Dialog */
.selected-grades-list {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  padding: 4px;
}

.selected-grade-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 4px;
  transition: background-color 0.2s ease;
}

.selected-grade-item:last-child {
  margin-bottom: 0;
}

.selected-grade-item:hover {
  background-color: #e8f5e9;
}

.selected-grade-item .grade-text {
  font-size: 14px;
  line-height: 1.4;
}

.delete-warning-banner {
  border-radius: 8px;
}

.delete-summary-card {
  border: 1px solid #ffcdd2;
  background: #fff5f5;
  border-radius: 10px;
  padding: 10px 12px;
}

.delete-summary-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #c62828;
  margin-bottom: 8px;
}

.delete-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
}

.delete-summary-item--full {
  grid-column: 1 / -1;
}

.delete-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.delete-value {
  font-size: 13px;
  color: #212121;
  font-weight: 500;
  line-height: 1.35;
}
</style>
